/**
 * Smith Protective — Outreach AI (Iter188).
 *
 * Full-screen email outreach tool. Two-pane workspace:
 *   Left  → filterable contacts list (email · pessoa · empresa · segmento)
 *   Right → compose panel: recipient auto-populated, Claire generates a
 *           personalised cold letter, user edits in a light rich-text
 *           editor, runs "Revisar Ortografia" (LLM-powered spellcheck),
 *           and fires via Resend. Post-send the contact row shows a
 *           "Email enviado" chip.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Send, Loader2, Search, Sparkles, SpellCheck2, Wand2, CheckCircle2,
  Mail, RefreshCw, X, ChevronDown, MapPin, Building2, Bold, Italic, Underline,
  List, ArrowLeft, BookmarkPlus, LibraryBig, Trash2, Languages, Paperclip,
  FileText, Image as ImageIcon, AlertCircle, MailCheck, Eye, MousePointerClick,
  AlertOctagon, Users, Rocket, Settings, Globe, Copy, ExternalLink,
  MessageSquare, Bell, Zap, BarChart3, Reply, ListChecks,
  SlidersHorizontal, Flag, Star, Ban, AlertTriangle, RotateCcw, Upload, Clock,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import HelpButton from "../../components/tutorials/HelpButton";
import OutreachTabBar from "./OutreachTabBar";
import DedupeModal from "../../components/smith/DedupeModal";
import SignatureEditor from "./SignatureEditor";
import RetryQueueTab from "./RetryQueueTab";
import ImportHubModal from "./ImportHubModal";
import { useLang } from "../../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

// Iter190 — surface any backend/Cloudflare failure as a user-friendly toast
// instead of the raw HTML error page. Always returns a stable string.
const friendlyError = (e, fallback = "Não foi possível completar a solicitação. Tente novamente.") => {
  const s = e?.response?.status;
  const data = e?.response?.data;
  // Structured backend error → prefer the message + code.
  if (data && typeof data === "object" && data.error?.message) {
    return `${data.error.message}${data.error.requestId ? ` (id ${data.error.requestId})` : ""}`;
  }
  // Legacy FastAPI shape.
  if (data && typeof data === "object" && data.detail) return data.detail;
  // Cloudflare/proxy 5xx returns HTML — never expose that raw.
  if (s === 502 || s === 503 || s === 504) {
    return "O servidor demorou mais que o esperado. Tente novamente em instantes.";
  }
  if (e?.code === "ECONNABORTED") return "Timeout — a operação demorou demais.";
  if (e?.message && !/^<!DOCTYPE/i.test(e.message)) return e.message;
  return fallback;
};

const TONE_OPTIONS = [
  { id: "consultative", i18n: "so.tone.consultative", label: "Consultivo" },
  { id: "urgent",       i18n: "so.tone.urgent",       label: "Urgente" },
  { id: "empathetic",   i18n: "so.tone.empathetic",   label: "Empático" },
  { id: "roi",          i18n: "so.tone.roi",          label: "ROI (números)" },
];

const LANGUAGE_OPTIONS = [
  { id: "pt", label: "🇧🇷 PT" },
  { id: "en", label: "🇺🇸 EN" },
  { id: "es", label: "🇪🇸 ES" },
];

// Iter316 · Heuristic lead-language detection. Reads the lead's state code
// and name to guess whether we should draft the cold email in PT / ES / EN.
// Rules — country-first, then LATAM state codes, then name pattern:
//   • state ∈ {BR states} → pt
//   • state ∈ {MX/AR/CO/CL states} → es
//   • state ∈ {US states} → en (explicit — Miami, LA, Puerto Rico use ES)
//   • name contains PT-only trigrams (ão / lh / nh) → pt
//   • name contains ES-only trigrams (ñ / ¿ / rr in surname) → es
//   • otherwise → null (leave the current language alone)
// This ONLY suggests — the user can still click any flag to override.
const _BR_STATES = new Set([
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB",
  "PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
]);
const _ES_STATES = new Set([
  // Mexican states (abbreviated)
  "AGU","BCN","BCS","CAM","CHP","CHH","CMX","COA","COL","DUR","GUA","GRO",
  "HID","JAL","MEX","MIC","MOR","NAY","NLE","OAX","PUE","QUE","ROO","SLP",
  "SIN","SON","TAB","TAM","TLA","VER","YUC","ZAC",
  // Argentine provinces (abbreviated)
  "CBA","MDZ","STA","TUC",
  // Puerto Rico (US territory but ES-first)
  "PR",
]);
function detectLeadLang(lead) {
  if (!lead) return null;
  const st = (lead.state || "").toUpperCase().trim();
  if (st && _BR_STATES.has(st)) return "pt";
  if (st && _ES_STATES.has(st)) return "es";
  // 2-letter US state codes → EN (explicit signal even for Miami/LA which
  // could otherwise trigger ES via name).
  if (st.length === 2 && !/[^A-Z]/.test(st)) return "en";
  const name = (lead.name || "").toLowerCase();
  if (!name) return null;
  if (/[ãõáâàêéíóôúç]/.test(name)) return "pt";
  if (/[ñáéíóúü]/.test(name) && /(rr|ll|ñ)/.test(name)) return "es";
  return null;
}

export default function SmithOutreach() {
  const { t } = useLang();
  // ── Contacts panel state
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [tier, setTier] = useState("");
  const [sentMap, setSentMap] = useState({});   // prospect_id → sent_at

  // Iter206 — email buckets + Claire auto-discovery + filter drawer
  const [emailBucket, setEmailBucket] = useState("available"); // available | missing | unresolvable
  const [autoDiscoveryBusy, setAutoDiscoveryBusy] = useState(false);
  const autoDiscoveredRef = useRef(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterCity, setFilterCity] = useState("");
  const [filterSegment, setFilterSegment] = useState("");
  const [filterFlagged, setFilterFlagged] = useState(false);
  const [filterSent, setFilterSent] = useState(false);
  // Iter228 · Auto-filter leads without email (persisted preference)
  const [filterOnlyWithEmail, setFilterOnlyWithEmail] = useState(() => {
    try { return localStorage.getItem("smith.outreach.onlyWithEmail") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("smith.outreach.onlyWithEmail",
                                  filterOnlyWithEmail ? "1" : "0"); }
    catch { /* ignore quota */ }
  }, [filterOnlyWithEmail]);
  // Iter285 · "Só falhados" filter — surface contacts whose outreach
  // failed, bounced, was skipped, is blacklisted or has an unverified
  // email AND was never successfully sent. Powers the batch cleanup.
  const [filterOnlyFailed, setFilterOnlyFailed] = useState(false);
  const [cleaningFailed, setCleaningFailed] = useState(false);
  // Iter296 · Autoclean widget — load status/preview lazily on mount.
  const [autoclean, setAutoclean] = useState(null);   // { last, preview }
  const [autocleanRunning, setAutocleanRunning] = useState(false);
  const [autocleanOpen, setAutocleanOpen] = useState(false);

  const loadAutocleanStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API}/smith/outreach/autoclean/status`);
      setAutoclean(data);
    } catch {
      setAutoclean({ ok: false, preview: { deletable_count: 0 } });
    }
  };
  useEffect(() => { loadAutocleanStatus(); /* on mount */ // eslint-disable-next-line
  }, []);

  const runAutoclean = async (dryRun) => {
    if (autocleanRunning) return;
    if (!dryRun && !window.confirm(
      "Executar limpeza real agora? Leads com ≥3 falhas serão excluídos permanentemente (safety rail preserva quem já foi enviado)."
    )) return;
    setAutocleanRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/autoclean/run?dry_run=${dryRun ? "true" : "false"}`);
      toast[dryRun ? "info" : "success"](
        dryRun
          ? `Simulação: ${data?.deletable_count || 0} lead(s) seriam apagados`
          : `Limpeza concluída: ${data?.deleted || 0} lead(s) removido(s)`,
        { duration: 4500 });
      await loadAutocleanStatus();
      if (!dryRun) loadContacts();
    } catch (e) {
      toast.error(friendlyError(e, "Falha no autoclean"));
    } finally { setAutocleanRunning(false); }
  };

  // ── Compose panel state
  const [selected, setSelected] = useState(null);   // full contact row
  const [tone, setTone] = useState("consultative");
  const [language, setLanguage] = useState("en");   // Iter224 · default EN (US market)
  // Iter318 · manual-override memory. When the rep flips translateTo for
  // the same prospect twice in a session, we PUT preferred_lang to the
  // backend so the next open goes straight to that language.
  const langOverrideCountsRef = useRef({});
  const [subject, setSubject] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [changes, setChanges] = useState([]);       // last spellcheck diff
  const editorRef = useRef(null);

  // ── Templates state (Iter189)
  const [templates, setTemplates] = useState([]);
  const [templatesMenuOpen, setTemplatesMenuOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  // ── Attachments & translation state (Iter190)
  const [attachments, setAttachments] = useState([]);   // [{storage_path,filename,mime_type,size_bytes}]
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [lastSendError, setLastSendError] = useState(null);
  const fileInputRef = useRef(null);

  // ── Delivery tracking + bulk send state (Iter192)
  const [deliveryMap, setDeliveryMap] = useState({});   // prospect_id → {status, sent_at, ...}
  const [selectedIds, setSelectedIds] = useState(new Set());  // prospect_ids checked for bulk
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);   // {batch_id, total, counts, items}
  const [bulkTemplateId, setBulkTemplateId] = useState("");  // Iter193 — optional template
  // ── Reply Inbox (Iter193)
  const [replies, setReplies] = useState([]);
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replyBody, setReplyBody] = useState(null);   // {id, body, ...}

  // ── Sender Identity Setup (Iter194)
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderStatus, setSenderStatus] = useState(null);   // full status object
  const [senderBusy, setSenderBusy] = useState(false);

  // ── Signature Editor (Iter228) + preview cache (Iter246)
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureExists, setSignatureExists] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState("");
  const loadSignature = () => axios.get(`${API}/smith/outreach/signature`)
      .then(({ data }) => {
        setSignatureExists(!!data.exists);
        // Prefer html_en for the compose preview; the send-time resolver
        // picks the language variant on its own based on the target lead.
        setSignatureHtml(data.html_en || data.html || "");
      })
      .catch(() => {});
  useEffect(() => { loadSignature(); }, []);

  // ── Iter246 · Deep Email Search (3 tentativas · 30s spacing) ──
  // `deepSearchProgress[pid] = attemptIndex` shows "tentativa X de 3" per
  // prospect. Cleared once the run finishes (or is aborted on unmount).
  const [deepSearchProgress, setDeepSearchProgress] = useState({});
  const deepSearchAbortRef = useRef(new Set());   // set of pids to abort
  useEffect(() => () => { deepSearchAbortRef.current.clear(); }, []);
  const setPidProgress = (pid, val) =>
    setDeepSearchProgress((m) => {
      const next = { ...m };
      if (val == null) delete next[pid];
      else next[pid] = val;
      return next;
    });
  const runDeepSearchBatch = async (pids, {
        maxAttempts = 3, spacingMs = 30_000,
  } = {}) => {
    if (!pids.length) return;
    const ids = [...new Set(pids)];
    ids.forEach((pid) => setPidProgress(pid, { attempt: 1, max: maxAttempts }));
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const stillMissing = ids.filter((pid) => !deepSearchAbortRef.current.has(pid));
      if (!stillMissing.length) break;
      try {
        const { data } = await axios.post(
          `${API}/smith/find-email/deep-search-bulk`,
          { prospect_ids: stillMissing });
        // Remove pids that got an email so we stop hammering.
        (data.results || []).forEach((r) => {
          if (r.ok && r.found && r.new_count > 0) {
            deepSearchAbortRef.current.add(r.prospect_id);
            setPidProgress(r.prospect_id, null);
          }
        });
      } catch (_e) { /* swallow — will retry or fall through */ }
      // Advance progress counter for pids that are still missing.
      ids.forEach((pid) => {
        if (!deepSearchAbortRef.current.has(pid)) {
          setPidProgress(pid, {
            attempt: Math.min(attempt + 1, maxAttempts),
            max: maxAttempts,
          });
        }
      });
      if (attempt < maxAttempts) {
        // Sleep 30s but bail early if all pids are resolved/aborted.
        const t0 = Date.now();
        while (Date.now() - t0 < spacingMs) {
          if (ids.every((pid) => deepSearchAbortRef.current.has(pid))) break;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    }
    // Cleanup: clear all remaining progress markers, refresh list.
    ids.forEach((pid) => setPidProgress(pid, null));
    deepSearchAbortRef.current.clear();
    loadContacts();
    toast.success("Busca profunda concluída — recarregando lista");
  };
  const retryDeepSearchOne = (pid) => runDeepSearchBatch([pid]);
  const retryDeepSearchBulk = () => {
    const pids = contacts
      .filter((c) => !c.email)
      .map((c) => c.prospect_id);
    if (!pids.length) return toast.info("Nenhum contato sem email para reprocessar");
    if (!window.confirm(`Retentar busca profunda em ${pids.length} contato(s)? Cada rodada leva ~90s (3 tentativas × 30s).`)) return;
    runDeepSearchBatch(pids);
  };

  // ── Auto-Nudge SMS 48h (Iter195)
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeCfg, setNudgeCfg] = useState(null);
  const [nudgeCfgDraft, setNudgeCfgDraft] = useState(null);
  const [nudgePending, setNudgePending] = useState(null);   // dry-run preview
  const [nudgeHistory, setNudgeHistory] = useState(null);   // {runs, recent_nudges}
  const [nudgeBusy, setNudgeBusy] = useState(false);

  // ── Dedupe modal (Iter196)
  const [dedupeOpen, setDedupeOpen] = useState(false);

  // ── Sub-tabs (Iter197): Compose | Contacts | Templates | Sent | Analytics
  const [activeTab, setActiveTab] = useState("compose");
  const [sendsHistory, setSendsHistory] = useState([]);   // populated from delivery-status

  // ── Attribution for analytics (Iter198)
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [appliedTemplateName, setAppliedTemplateName] = useState(null);
  const [breakdown, setBreakdown] = useState(null);       // {by_tone, by_template}
  // Iter235 · signature analytics (PT vs EN performance)
  const [sigAnalytics, setSigAnalytics] = useState(null);

  // ── Auto email discovery (Iter199) — Buscar email com IA
  const [findingEmail, setFindingEmail] = useState(false);

  // ── Bulk find-email (Iter200)
  const [bulkFindOpen, setBulkFindOpen] = useState(false);
  const [bulkFindRunning, setBulkFindRunning] = useState(false);
  const [bulkFindProgress, setBulkFindProgress] = useState({ processed: 0, total: 0, newEmails: 0, results: [] });

  // ── AI Search intelligence injection (Iter203 · Phase 4)
  const [aiCompanyId, setAiCompanyId] = useState(null);
  const [aiBrief, setAiBrief] = useState(null);

  // ── Enroll in sequence (Iter200)
  const [sequences, setSequences] = useState([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const findEmailForSelected = async () => {
    if (!selected?.prospect_id) {
      toast.error("Selecione um contato primeiro"); return;
    }
    setFindingEmail(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/prospects/${selected.prospect_id}/find-email`,
        {}, { timeout: 120000 });
      if (data.found && (data.emails || []).length) {
        // Prefer a "new" email; else pick the first available
        const pick = (data.new_emails && data.new_emails[0]) || data.emails[0];
        toast.success(`Encontrei ${data.count} email(s) via ${pick.source || "AI"} — selecionando ${pick.email}`);
        // Update selected in-place + refresh contacts list
        setSelected({
          ...selected,
          email: pick.email,
          name: selected.name || pick.name,
          title: selected.title || pick.title,
          source: pick.source,
        });
        loadContacts();
      } else {
        const stats = data.source_status || {};
        const details = Object.entries(stats).map(([k, v]) => `${k}: ${v}`).join(" · ");
        toast.warning(`Nenhum email encontrado. ${details}`);
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha na busca de email"));
    } finally { setFindingEmail(false); }
  };

  // Iter200 — Bulk find-email runs Apollo→Hunter→web-scrape on every
  // prospect without a DM email. Chunks calls to 20 per HTTP request so
  // the UI can render live progress (the backend endpoint itself blocks
  // until each chunk finishes; we call it in a loop of chunks).
  const runBulkFindEmail = async () => {
    if (!confirm("Buscar emails em lote nos prospects sem contato? Pode levar alguns minutos.")) return;
    setBulkFindRunning(true);
    setBulkFindProgress({ processed: 0, total: 0, newEmails: 0, results: [] });
    try {
      // First get total count of prospects without email (via contacts endpoint)
      const targets = [];
      // 20 per chunk keeps each HTTP call under 2min for slow Apollo/Hunter
      let hasMore = true;
      let totalDiscovered = 0;
      let allResults = [];
      while (hasMore) {
        const { data } = await axios.post(
          `${API}/smith/find-email/bulk?limit=20`, {}, { timeout: 180000 });
        totalDiscovered += data.total_new_emails || 0;
        allResults = [...allResults, ...(data.results || [])];
        setBulkFindProgress({
          processed: allResults.length,
          total: allResults.length + (data.targets === 20 ? 20 : 0),   // estimate
          newEmails: totalDiscovered,
          results: allResults,
        });
        // Stop if the last chunk returned fewer than 20 (means no more targets)
        if ((data.targets || 0) < 20) hasMore = false;
        // Safety cap — never process more than 200 in one bulk session
        if (allResults.length >= 200) hasMore = false;
      }
      toast.success(`Bulk find-email concluído · ${totalDiscovered} emails novos em ${allResults.length} prospects processados`);
      loadContacts();
    } catch (e) {
      toast.error(friendlyError(e, "Falha no bulk find-email"));
    } finally { setBulkFindRunning(false); }
  };

  // Iter200 — Enroll current selected contact into a chosen sequence
  const loadSequences = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/sequences`);
      setSequences(data.items || []);
    } catch (_) { /* silent */ }
  };
  const enrollInSequence = async (seq) => {
    if (!selected?.prospect_id) {
      toast.error("Selecione um contato antes"); return;
    }
    if (!confirm(`Inscrever "${selected.name || selected.email}" na cadência "${seq.name}"?`)) return;
    setEnrolling(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/sequences/${seq.id}/enroll`,
        {
          prospect_ids: [selected.prospect_id],
          to_email_override: selected.email || null,
        });
      if ((data.enrolled || []).length) {
        toast.success(`Inscrito! Primeiro email em ${new Date(data.enrolled[0].next_run_at).toLocaleString("pt-BR")}`);
      } else if ((data.skipped || []).length) {
        toast.warning(`Não inscrito: ${data.skipped[0]?.reason || "unknown"}`);
      }
      setEnrollOpen(false);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao inscrever"));
    } finally { setEnrolling(false); }
  };

  // ── Load contacts (reuses /smith/contacts/emails endpoint)
  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (source) params.set("source", source);
      if (tier) params.set("tier", tier);
      // Iter206 — email bucket + filters
      params.set("email_state", emailBucket);
      if (filterCity)     params.set("city", filterCity);
      if (filterSegment)  params.set("segment", filterSegment);
      if (filterFlagged)  params.set("flagged_only", "true");
      if (filterSent)     params.set("sent_only", "true");
      params.set("limit", "200");
      const { data } = await axios.get(`${API}/smith/contacts/emails?${params}`);
      setContacts(data.items || []);
      setContactsTotal(data.total || 0);
      // Iter206 — auto-trigger discovery on "missing" tab if the bucket
      // has prospects and we haven't already triggered this session.
      if (emailBucket === "missing" && (data.total || 0) > 0
          && !autoDiscoveredRef.current && !autoDiscoveryBusy) {
        autoDiscoveredRef.current = true;
        autoDiscoverMissingEmails();
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao carregar contatos"));
    } finally { setLoadingContacts(false); }
  };
  useEffect(() => {
    const t = setTimeout(loadContacts, 280);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, source, tier, emailBucket, filterCity, filterSegment, filterFlagged, filterSent]);

  // Iter208 — After contacts refresh, cross-reference their emails
  // against the tenant blacklist so we can render the "Bloqueado" badge
  // and disable bulk-select checkboxes.
  useEffect(() => {
    const emails = (contacts || [])
      .map((c) => (c.email || "").trim().toLowerCase())
      .filter(Boolean);
    if (!emails.length) { setBlacklistedSet(new Set()); return; }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/smith/outreach/blacklist/check`, { emails });
        setBlacklistedSet(new Set(
          (data.blacklisted || []).map((b) => b.email.toLowerCase())));
      } catch (_) { /* silent */ }
    })();
  }, [contacts]);

  // Iter206 — auto-discovery of missing emails via Claire's cascade.
  const autoDiscoverMissingEmails = async () => {
    setAutoDiscoveryBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/find-email/bulk`,
        { limit: 25 }, { timeout: 240000 });
      const newE = data?.total_new_emails || 0;
      const unres = data?.unresolvable || 0;
      if (newE > 0) toast.success(`Claire descobriu ${newE} email(s) novo(s)`);
      if (unres > 0) toast.info(`${unres} prospect(s) marcado(s) como não-resolvíveis (repositório)`);
      loadContacts();
    } catch (ex) {
      // silent fail — user can still interact with the list
    } finally { setAutoDiscoveryBusy(false); }
  };

  // Iter207 — Retry the Repositório bucket on demand.
  const [retryingRepo, setRetryingRepo] = useState(false);
  // Iter208 — Blacklist cross-reference for the current contacts list.
  const [blacklistedSet, setBlacklistedSet] = useState(new Set());
  // Iter208 — Bulk resend of failed sends.
  const [resendingBulk, setResendingBulk] = useState(false);
  // Iter211 — Auto Outreach Drafts (Claire-generated pending drafts).
  const [drafts, setDrafts] = useState([]);
  const [draftsPending, setDraftsPending] = useState(0);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftEditing, setDraftEditing] = useState(null);
  const [draftSending, setDraftSending] = useState({});

  const loadDrafts = async () => {
    setDraftsLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/drafts`);
      setDrafts(data.items || []);
      setDraftsPending(data.total_pending || 0);
    } catch (_) { /* silent */ }
    finally { setDraftsLoading(false); }
  };
  useEffect(() => { loadDrafts(); }, []);
  useEffect(() => {
    if (activeTab === "drafts") loadDrafts();
    // eslint-disable-next-line
  }, [activeTab]);

  const draftSend = async (d) => {
    setDraftSending((s) => ({ ...s, [d.id]: true }));
    try {
      await axios.post(`${API}/smith/outreach/drafts/${d.id}/send`);
      toast.success("Draft enviado");
      loadDrafts();
    } catch (ex) {
      toast.error(friendlyError(ex, "Falha ao enviar draft"));
    } finally {
      setDraftSending((s) => { const n = { ...s }; delete n[d.id]; return n; });
    }
  };
  const draftDiscard = async (d) => {
    if (!confirm("Descartar este draft?")) return;
    try {
      await axios.delete(`${API}/smith/outreach/drafts/${d.id}`);
      toast.success("Draft descartado");
      loadDrafts();
    } catch (ex) { toast.error(friendlyError(ex, "Falha ao descartar")); }
  };
  const draftSaveEdit = async () => {
    if (!draftEditing) return;
    try {
      await axios.put(`${API}/smith/outreach/drafts/${draftEditing.id}`,
        { subject: draftEditing.subject, body: draftEditing.body });
      toast.success("Draft atualizado");
      setDraftEditing(null);
      loadDrafts();
    } catch (ex) { toast.error(friendlyError(ex, "Falha ao salvar edição")); }
  };
  const failedCount = useMemo(() => Object.values(deliveryMap).filter(
    // Iter233 · Include "skipped" so the button reflects the true
    // retry queue (blacklist + sem-body archives count as retriable).
    (d) => ["failed", "bounced", "skipped"].includes(d?.status)).length, [deliveryMap]);
  // Iter285 · Count of visible contacts that qualify as "failed/stuck"
  // — powers the "Só falhados" toggle badge + the cleanup button label.
  // Reads deliveryMap/sentMap/blacklistedSet so it auto-refreshes on
  // any of those state changes.
  const failedContactsCount = useMemo(
    () => contacts.filter((c) => {
      if (!c || c.email_sent_at) return false;
      if (sentMap[c.prospect_id]) return false;
      const d = deliveryMap[c.prospect_id];
      if (d && ["failed", "bounced", "skipped"].includes(d.status)) return true;
      const email = (c.email || "").toLowerCase();
      if (email && blacklistedSet.has(email)) return true;
      if (email && !c.email_verified
          && !["apollo", "hunter"].includes((c.source || "").toLowerCase())) {
        return true;
      }
      if (c.email_unresolvable_at) return true;
      if (!email) return true;
      return false;
    }).length,
    [contacts, deliveryMap, sentMap, blacklistedSet]);
  // Iter208 — Bulk modal filter dropdowns.
  const [bulkFilterVerified, setBulkFilterVerified] = useState("");
  const [bulkFilterSource, setBulkFilterSource] = useState("");
  const [bulkFilterTier, setBulkFilterTier] = useState("");
  // Iter224 — "Apenas com email" toggle (default ON) so reps never dispatch
  // to unmapped contacts by accident.
  const [bulkOnlyWithEmail, setBulkOnlyWithEmail] = useState(true);
  const bulkFilteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (!selectedIds.has(c.prospect_id)) return false;
      if (bulkFilterSource && (c.source || "").toLowerCase() !== bulkFilterSource) return false;
      if (bulkFilterTier && c.tier !== bulkFilterTier) return false;
      if (bulkFilterVerified === "verified"
          && !(c.email_verified
                || ["apollo","hunter"].includes((c.source||"").toLowerCase())))
        return false;
      if (bulkFilterVerified === "unverified"
          && !((!c.email_verified)
                && !["apollo","hunter"].includes((c.source||"").toLowerCase())
                && !c.email_unresolvable_at))
        return false;
      if (bulkFilterVerified === "unresolvable" && !c.email_unresolvable_at) return false;
      if (bulkOnlyWithEmail && !c.email) return false;
      return true;
    });
  }, [contacts, selectedIds, bulkFilterVerified, bulkFilterSource, bulkFilterTier, bulkOnlyWithEmail]);
  // Iter224 — dispatch stats so the modal + toasts are honest about what
  // will and won't be sent.
  const bulkStats = useMemo(() => {
    const selected = contacts.filter((c) => selectedIds.has(c.prospect_id));
    const withEmail    = selected.filter((c) => !!c.email);
    const withoutEmail = selected.filter((c) => !c.email);
    const blacklisted  = withEmail.filter((c) =>
      blacklistedSet.has((c.email || "").toLowerCase()));
    const ready = bulkFilteredContacts.filter((c) =>
      c.email && !blacklistedSet.has((c.email || "").toLowerCase()));
    // Iter292 · Unverified/never-sent leads inside the selection — feeds
    // the new "Excluir N não-confirmados" button in the Bulk toolbar.
    const unverifiedUnsent = selected.filter((c) => {
      if (c.email_sent_at || sentMap[c.prospect_id]) return false;
      const email = (c.email || "").toLowerCase();
      if (email && blacklistedSet.has(email)) return true;
      if (email && !c.email_verified
          && !["apollo", "hunter"].includes((c.source || "").toLowerCase())) return true;
      return false;
    });
    return {
      selected:     selected.length,
      with_email:   withEmail.length,
      no_email:     withoutEmail.length,
      blacklisted:  blacklisted.length,
      ready:        ready.length,
      unverified_unsent: unverifiedUnsent.length,
    };
  }, [contacts, selectedIds, blacklistedSet, bulkFilteredContacts, sentMap]);
  const bulkBlacklistCount = bulkStats.blacklisted;
  // Iter224 — 1-click cleanup: drop every selected row that has no email.
  // Compute the diff BEFORE calling setSelectedIds so the toast count is
  // deterministic even under React StrictMode's double-invocation of the
  // functional updater (which would otherwise fire the toast twice with
  // stale intermediate state).
  const removeSelectedWithoutEmail = () => {
    const keep = new Set();
    let dropped = 0;
    contacts.forEach((c) => {
      if (!selectedIds.has(c.prospect_id)) return;
      if (c.email) keep.add(c.prospect_id);
      else dropped += 1;
    });
    setSelectedIds(keep);
    if (dropped > 0) toast.success(`${dropped} contato${dropped === 1 ? "" : "s"} sem email removido${dropped === 1 ? "" : "s"} da seleção`);
    else toast.info("Nada para remover — todos selecionados têm email");
  };

  // Iter285 · Failed-lead helper — a contact counts as "failed / stuck"
  // when it NEVER successfully sent AND has at least one of these blockers:
  //   • delivery.status ∈ {failed, bounced, skipped}
  //   • email is on the tenant blacklist
  //   • email exists but is unverified (no Apollo/Hunter source)
  //   • the CRM archived it as email_unresolvable
  //   • the contact has no email at all (can never be dispatched)
  const isFailedContact = (c) => {
    if (!c) return false;
    if (c.email_sent_at) return false;       // already delivered → keep
    if (sentMap[c.prospect_id]) return false;
    const d = deliveryMap[c.prospect_id];
    if (d && ["failed", "bounced", "skipped"].includes(d.status)) return true;
    const email = (c.email || "").toLowerCase();
    if (email && blacklistedSet.has(email)) return true;
    if (email && !c.email_verified
        && !["apollo", "hunter"].includes((c.source || "").toLowerCase())) {
      return true;   // unverified public heuristic → likely to fail
    }
    if (c.email_unresolvable_at) return true;
    if (!email) return true;                 // no email → cannot dispatch
    return false;
  };

  // Iter293 · Snapshot buffer for "Desfazer" toast. Cleared 6s after
  // last delete OR on successful restore.
  const undoBufferRef = useRef({ docs: [], timer: null });

  const _armUndo = (deletedDocs) => {
    if (!Array.isArray(deletedDocs) || deletedDocs.length === 0) return;
    // Append to the buffer so consecutive deletes stack.
    undoBufferRef.current.docs = [
      ...undoBufferRef.current.docs,
      ...deletedDocs,
    ];
    if (undoBufferRef.current.timer) clearTimeout(undoBufferRef.current.timer);
    const total = undoBufferRef.current.docs.length;
    const restore = async () => {
      const snapshot = undoBufferRef.current.docs;
      undoBufferRef.current.docs = [];
      if (undoBufferRef.current.timer) clearTimeout(undoBufferRef.current.timer);
      undoBufferRef.current.timer = null;
      try {
        const { data } = await axios.post(
          `${API}/smith/prospects/bulk-restore`,
          { prospects: snapshot });
        toast.success(`↩ ${data?.restored || 0} lead(s) restaurado(s)`);
        loadContacts();
      } catch (e) {
        toast.error(friendlyError(e, "Falha ao restaurar"));
      }
    };
    toast(`${total} lead(s) excluído(s)`, {
      description: "Você tem 6 segundos para desfazer",
      duration: 6000,
      action: { label: "Desfazer", onClick: restore },
      dismissible: true,
    });
    undoBufferRef.current.timer = setTimeout(() => {
      undoBufferRef.current.docs = [];
      undoBufferRef.current.timer = null;
    }, 7000);
  };

  // Iter292 · One-click delete of a single unverified/blocked contact.
  // Same safety rail as bulk cleanup — backend refuses to remove
  // prospects that already have pipeline activity.
  const deleteContactRow = async (c) => {
    if (!c?.prospect_id) return;
    if (c.email_sent_at || sentMap[c.prospect_id]) {
      toast.info("Não é possível excluir — este lead já foi enviado");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/smith/prospects/kanban-bulk-delete`,
        { prospect_ids: [c.prospect_id] });
      if (data?.deleted) {
        _armUndo(data?.deleted_docs || []);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(c.prospect_id);
          return next;
        });
        loadContacts();
      } else {
        toast.info("Lead tem histórico de envio — preservado");
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao excluir"));
    }
  };

  // Iter285 · Batch cleanup — hard-delete every contact that matches
  // `isFailedContact`. Falls back to safe pipeline stages
  // (New Prospect / Researching / Junk) via the existing bulk-delete
  // endpoint, which refuses to remove prospects with activity history.
  const cleanupFailedContacts = async () => {
    const targets = contacts.filter(isFailedContact);
    if (targets.length === 0) {
      toast.info("Nada para limpar — nenhum lead falhado/bloqueado na lista visível");
      return;
    }
    const ids = [...new Set(targets.map((c) => c.prospect_id).filter(Boolean))];
    if (!ids.length) {
      toast.info("Nada para limpar — nenhum prospect_id válido");
      return;
    }
    if (!window.confirm(
      `Excluir permanentemente ${ids.length} lead(s) falhados/bloqueados/sem-email?\n\n`
      + `Só serão apagados os que estão em pipeline "New Prospect / Researching / Junk".\n`
      + `Leads com histórico de envio bem-sucedido são preservados.`
    )) return;
    setCleaningFailed(true);
    try {
      // API allows up to 100 ids per call — chunk when needed.
      let deleted = 0;
      let blocked = 0;
      const allDocs = [];
      for (let i = 0; i < ids.length; i += 100) {
        const chunk = ids.slice(i, i + 100);
        const { data } = await axios.post(
          `${API}/smith/prospects/kanban-bulk-delete`,
          { prospect_ids: chunk });
        deleted += data?.deleted || 0;
        blocked += data?.blocked || 0;
        if (Array.isArray(data?.deleted_docs)) allDocs.push(...data.deleted_docs);
      }
      _armUndo(allDocs);
      if (blocked) toast.info(`${blocked} preservado(s) por histórico ativo`);
      // Drop deleted ids from selection + refresh list.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((pid) => next.delete(pid));
        return next;
      });
      loadContacts();
    } catch (e) {
      toast.error(friendlyError(e, "Falha na limpeza em massa"));
    } finally { setCleaningFailed(false); }
  };

  // Iter292 · Batch delete of every selected unverified/blocked lead
  // that was never sent successfully. Reuses the same safety-railed
  // `kanban-bulk-delete` endpoint the row-level trash icon uses.
  const deleteSelectedUnverified = async () => {
    const targets = contacts.filter((c) => {
      if (!selectedIds.has(c.prospect_id)) return false;
      if (c.email_sent_at || sentMap[c.prospect_id]) return false;
      const email = (c.email || "").toLowerCase();
      if (email && blacklistedSet.has(email)) return true;
      if (email && !c.email_verified
          && !["apollo", "hunter"].includes((c.source || "").toLowerCase())) return true;
      return false;
    });
    if (targets.length === 0) {
      toast.info("Nada para excluir — nenhum não-confirmado na seleção");
      return;
    }
    if (!window.confirm(
      `Excluir permanentemente ${targets.length} lead(s) não-confirmado(s) da seleção?\n\n`
      + `Somente leads não enviados são apagados.`
    )) return;
    setCleaningFailed(true);
    try {
      const ids = [...new Set(targets.map((c) => c.prospect_id).filter(Boolean))];
      let deleted = 0;
      let blocked = 0;
      const allDocs = [];
      for (let i = 0; i < ids.length; i += 100) {
        const chunk = ids.slice(i, i + 100);
        const { data } = await axios.post(
          `${API}/smith/prospects/kanban-bulk-delete`,
          { prospect_ids: chunk });
        deleted += data?.deleted || 0;
        blocked += data?.blocked || 0;
        if (Array.isArray(data?.deleted_docs)) allDocs.push(...data.deleted_docs);
      }
      _armUndo(allDocs);
      if (blocked) toast.info(`${blocked} preservado(s) por histórico`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((pid) => next.delete(pid));
        return next;
      });
      loadContacts();
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao excluir seleção"));
    } finally { setCleaningFailed(false); }
  };

  // Iter229 · Enrich all leads without email (Apollo→Hunter→Apify Turbo)
  const [enrichingBulk, setEnrichingBulk] = useState(false);
  const enrichMissingEmailsBulk = async () => {
    const targets = contacts.filter((c) => !c.email || !c.email.includes("@"));
    if (targets.length === 0) {
      toast.info("Todos os leads visíveis já têm e-mail");
      return;
    }
    if (!confirm(
      `Enriquecer ${targets.length} lead(s) sem e-mail com Turbo `
      + `(Apollo → Hunter → Apify)? Isso pode levar 1-2 min e usar créditos.`
    )) return;
    setEnrichingBulk(true);
    try {
      const ids = [...new Set(targets.map((c) => c.prospect_id).filter(Boolean))];
      const { data } = await axios.post(`${API}/smith/find-email/bulk`,
        { prospect_ids: ids, limit: Math.min(ids.length, 200) },
        { timeout: 180000 });
      const found = (data.results || []).reduce((s, r) => s + (r.new_count || 0), 0);
      const unresolvable = data.unresolvable_now || 0;
      toast.success(
        `${found} e-mail(s) encontrados via Turbo` +
        (unresolvable ? ` · ${unresolvable} arquivados no Repositório` : ""),
        { duration: 5000 });
      loadContacts();   // refresh list
    } catch (e) {
      toast.error(friendlyError(e, "Falha no enriquecimento em massa"));
    } finally { setEnrichingBulk(false); }
  };

  // Iter228 · Retry drawer state — opened when the original send has no
  // archived body OR the operator wants to tweak subject/body before retry.
  const [retryDrawer, setRetryDrawer] = useState(null);
  //  shape: { send_id, to, subject, body, contact, prospect_id,
  //             vision_source? { analysis_id, preset_label } }

  // Iter260 · Vision → Retry Drawer bridge
  const [visionPrefilling, setVisionPrefilling] = useState(false);
  const [visionDiff, setVisionDiff] = useState(null);
  //  shape: { subject, body, current_subject, current_body,
  //             analysis_id, preset_label }

  const prefillRetryFromVision = async ({ send_id, prospect_id, analysis_id } = {}) => {
    if (visionPrefilling) return;
    setVisionPrefilling(true);
    try {
      const payload = {};
      if (send_id) payload.send_id = send_id;
      if (prospect_id) payload.prospect_id = prospect_id;
      if (analysis_id) payload.analysis_id = analysis_id;
      const { data } = await axios.post(
        `${API}/smith/vision/prefill-retry`, null,
        { params: payload, timeout: 45000 });
      const hasCurrent = Boolean(
        (data.current_body || "").trim() ||
        (data.current_subject || "").trim());
      if (hasCurrent) {
        // Open diff preview modal — operator confirms before overwrite.
        setVisionDiff({
          subject: data.subject,
          body: data.body,
          current_subject: data.current_subject || "",
          current_body: data.current_body || "",
          analysis_id: data.analysis_id,
          preset_label: data.preset_label,
          send_id: data.send_id,
          prospect_id: data.prospect_id,
          company_name: data.company_name,
          image_urls: data.image_urls || [],
          image_labels: data.image_labels || [],
          has_inlineable_images: !!data.has_inlineable_images,
        });
      } else {
        applyVisionPrefill({
          subject: data.subject,
          body: data.body,
          analysis_id: data.analysis_id,
          preset_label: data.preset_label,
          send_id: data.send_id,
          prospect_id: data.prospect_id,
          company_name: data.company_name,
          image_urls: data.image_urls || [],
          image_labels: data.image_labels || [],
          has_inlineable_images: !!data.has_inlineable_images,
        });
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao pré-preencher a partir da Vision"));
    } finally {
      setVisionPrefilling(false);
    }
  };

  const applyVisionPrefill = ({ subject, body, analysis_id, preset_label,
                                    send_id, prospect_id, company_name,
                                    image_urls, image_labels,
                                    has_inlineable_images }) => {
    setRetryDrawer((cur) => {
      const base = cur || {
        send_id,
        to: "",
        subject: "",
        body: "",
        prospect_id,
        contact: prospect_id
          ? contacts.find((c) => c.prospect_id === prospect_id)
          : undefined,
        reason: "Pré-preenchido pela Vision Analysis · edite antes de enviar",
      };
      return {
        ...base,
        subject,
        body,
        prospect_id: prospect_id || base.prospect_id,
        vision_source: {
          analysis_id, preset_label, company_name,
          image_urls: image_urls || [],
          image_labels: image_labels || [],
          has_inlineable_images: !!has_inlineable_images,
        },
      };
    });
    setVisionDiff(null);
    toast.success("Cold-email da Vision preenchido no retry drawer");
  };

  // Iter254 · Import Hub modal + Claire contextual-draft loading flag.
  const [importHubOpen, setImportHubOpen] = useState(false);
  const [claireDrafting, setClaireDrafting] = useState(false);

  // Iter254 · Ask Claire to redraft the body of the current retry item.
  const runClaireDraft = async () => {
    if (!retryDrawer) return;
    setClaireDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/claire-draft`,
        {
          subject: retryDrawer.subject || "",
          recipient_email: retryDrawer.to || "",
          language: "pt",
        },
        { timeout: 40000 });
      setRetryDrawer((cur) => cur ? { ...cur, body: data.body } : cur);
      if (data.fallback_used) {
        toast.warning("Claire offline — usei um template de emergência. Edite antes de enviar.");
      } else {
        toast.success(`Claire redigiu ${data.body?.length || 0} caracteres`);
      }
    } catch (e) {
      toast.error(friendlyError(e, "Claire falhou — cole um template manual."));
    } finally { setClaireDrafting(false); }
  };

  const resendSingle = async (send_id, toEmail, opts = {}) => {
    if (!send_id) return;
    try {
      await axios.post(`${API}/smith/outreach/resend/${send_id}`, {
        ...(toEmail ? { to_email: toEmail } : {}),
        ...(opts.body ? { body: opts.body } : {}),
        ...(opts.subject ? { subject: opts.subject } : {}),
        ...(opts.source_kind ? { source_kind: opts.source_kind } : {}),
        ...(opts.inline_from_vision_id
              ? { inline_from_vision_id: opts.inline_from_vision_id }
              : {}),
      });
      toast.success(opts.inline_from_vision_id
        ? "Reenvio com fotos da Vision enfileirado"
        : "Reenvio enfileirado");
      setRetryDrawer(null);
      setTimeout(loadDeliveryStatus, 1500);
    } catch (e) {
      // Iter228 · Backend returns 422 when body was never archived. Open
      // the inline edit drawer instead of a raw error.
      if (e?.response?.status === 422) {
        const contact = contacts.find((c) => c.email === toEmail);
        setRetryDrawer({
          send_id, to: toEmail,
          subject: opts.subject || "",
          body: opts.body || "",
          contact,
          prospect_id: contact?.prospect_id,
          reason: e?.response?.data?.detail || "Body original não arquivado",
        });
        toast.warning("Body original não foi arquivado — edite antes de reenviar");
        return;
      }
      toast.error(friendlyError(e, "Falha no reenvio"));
    }
  };
  const resendFailedBulk = async () => {
    // Iter233 · When there are no failed items, fall back to the
    // server-side retry-queue count so the operator gets a clear
    // reason instead of a silent no-op.
    if (failedCount === 0) {
      try {
        const { data } = await axios.get(`${API}/smith/outreach/retry-queue?limit=1`);
        if ((data?.total || 0) === 0) {
          toast.info("Nenhum envio falho na fila — tudo entregue ✓");
        } else {
          toast.warning(
            `Fila tem ${data.total} envio(s) falhos que não aparecem aqui — abra a aba "Retry Queue" para gerenciar.`,
            { duration: 6000 });
          setActiveTab("retry");
        }
      } catch {
        toast.info("Nenhum envio falho detectado nesta lista");
      }
      return;
    }
    if (!confirm(`Reenviar ${failedCount} email(s) com falha?`)) return;
    setResendingBulk(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/resend-failed/bulk`);
      const parts = [`${data.resent || 0} enfileirados`];
      if (data.skipped_blacklist) parts.push(`${data.skipped_blacklist} bloqueados (blacklist)`);
      if (data.skipped_no_body)   parts.push(`${data.skipped_no_body} sem body arquivado`);
      const msg = parts.join(" · ");
      if ((data.resent || 0) === 0 && (data.skipped_no_body || 0) > 0) {
        toast.warning(`${msg}. Abra a aba "Retry Queue" para editar & reenviar os sem body.`,
          { duration: 8000 });
      } else if ((data.resent || 0) === 0) {
        toast.info(msg);
      } else {
        toast.success(msg);
      }
      setTimeout(loadDeliveryStatus, 2500);
    } catch (e) {
      toast.error(friendlyError(e, "Falha no reenvio em lote"));
    } finally { setResendingBulk(false); }
  };
  const retryUnresolvable = async () => {
    if (!confirm("Retentar todos os prospects do Repositório? A Claire vai rodar Apollo → Hunter → Apify novamente.")) return;
    setRetryingRepo(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/find-email/retry-unresolvable`,
        { limit: 50, older_than_hours: 0 },
        { timeout: 300000 });
      const rec = data?.recovered || 0;
      const still = data?.still_unresolvable || 0;
      if (rec > 0) toast.success(`Claire resgatou ${rec} lead(s) do repositório`);
      else toast.info(`Nenhum email novo (${still} ainda sem resposta pública)`);
      loadContacts();
    } catch (ex) {
      toast.error(friendlyError(ex, "Falha ao retentar repositório"));
    } finally { setRetryingRepo(false); }
  };
  const flagDm = async (row) => {
    try {
      const nowFlagged = !row.flagged;
      await axios.post(`${API}/smith/contacts/${row.prospect_id}/dm/flag`,
        { email: row.email, flagged: nowFlagged });
      setContacts((prev) => prev.map((r) =>
        (r.prospect_id === row.prospect_id && r.email === row.email)
          ? { ...r, flagged: nowFlagged } : r));
      toast.success(nowFlagged ? "Contato sinalizado" : "Sinalização removida");
    } catch (ex) {
      toast.error(friendlyError(ex, "Falha ao sinalizar"));
    }
  };

  // ── URL-based context (Iter198 + Iter220) — deep-link from Kanban / Map /
  //   Prospect Detail / Command Center "Contact Today" green arrow:
  //   /smith/outreach?prospect_id=<pid>[&email=<e>][&tab=compose]
  // Auto-selects the first matching contact once the list loads. If the
  // prospect has no DM email cached (so it's not in the "available" bucket),
  // fall back to hydrating a synthetic contact from GET /smith/prospects/<pid>
  // — this guarantees the rep never lands on an empty Compose panel.
  const [searchParams] = useSearchParams();
  const hydrated_pid_ref = useRef(null);   // guard: avoid re-hydrating the same pid twice
  // Iter222 · consume VS-Sequence draft on arrival — hand-off from
  // Vehicle Security's OutreachSequenceModal "Send this email now".
  const readVsDraft = () => {
    const flag = searchParams.get("vs_draft");
    if (flag !== "1") return null;
    try {
      const raw = sessionStorage.getItem("vs_outreach_draft");
      if (!raw) return null;
      const draft = JSON.parse(raw);
      // Only accept a fresh draft (<10 min old).
      if (draft?.generated_at) {
        const ageMs = Date.now() - new Date(draft.generated_at).getTime();
        if (ageMs > 10 * 60 * 1000) return null;
      }
      return draft;
    } catch { return null; }
  };
  const clearVsDraft = () => {
    try { sessionStorage.removeItem("vs_outreach_draft"); } catch { /* silent */ }
  };
  const applyVsDraft = (row, draft) => {
    // Direct-set — mirrors pickContact() side-effects but skips generateLetter
    // so the operator's crafted subject/body isn't overwritten by the LLM.
    setSelected(row);
    setChanges([]);
    setAttachments([]);
    setLastSendError(null);
    setAppliedTemplateId(null);
    setAppliedTemplateName(null);
    setSubject(draft.subject || "");
    // editorRef may not be mounted yet (tab just switching to compose) —
    // retry briefly until it is.
    let attempts = 0;
    const inject = () => {
      if (editorRef.current) {
        editorRef.current.innerText = draft.body_plain || "";
        return;
      }
      if (attempts++ < 20) setTimeout(inject, 50);
    };
    setTimeout(inject, 60);
  };
  useEffect(() => {
    const wanted_pid = searchParams.get("prospect_id");
    const wanted_email = (searchParams.get("email") || "").toLowerCase();
    const wanted_vs_lead = searchParams.get("vs_lead_id");
    const wanted_tab = searchParams.get("tab");
    if (wanted_tab && ["compose","contacts","templates","sent","analytics","retry"].includes(wanted_tab)) {
      setActiveTab(wanted_tab);
    }
    if (!wanted_pid && !wanted_email && !wanted_vs_lead) return;
    // Skip if we already have a matching contact selected (avoid loops)
    if (selected && (selected.prospect_id === wanted_pid || (selected.email || "").toLowerCase() === wanted_email)) return;

    // Iter222 — always resolve the draft up-front so downstream branches
    // can decide to skip generateLetter.
    const draft = readVsDraft();

    // 1) Try the loaded contacts list first (fast path).
    if (loadingContacts) {
      // Still fetching — wait for the list before deciding whether to run
      // the fallback (Iter220). Prevents "premature hydration".
      return;
    }
    if (contacts.length > 0) {
      const match = contacts.find((c) =>
        (wanted_pid && c.prospect_id === wanted_pid) ||
        (wanted_email && (c.email || "").toLowerCase() === wanted_email));
      if (match) {
        if (draft) {
          applyVsDraft(match, draft);
          clearVsDraft();
          setActiveTab("compose");
          toast.success(`Draft VS aplicado em "${match.name || match.email}"`);
        } else {
          pickContact(match);
          setActiveTab("compose");
          toast.success(`Contato "${match.name || match.email}" pré-carregado`);
        }
        hydrated_pid_ref.current = wanted_pid || match.prospect_id;
        return;
      }
    }

    // 2a) Iter222 fallback — VS lead hand-off. No smith prospect link, so
    //     build the synthetic contact directly from the VS lead payload
    //     that OutreachSequenceModal snapshotted into sessionStorage.
    if (wanted_vs_lead && draft?.lead_snapshot
          && hydrated_pid_ref.current !== `vs:${wanted_vs_lead}`) {
      hydrated_pid_ref.current = `vs:${wanted_vs_lead}`;
      const snap = draft.lead_snapshot;
      const synthetic = {
        prospect_id:  null,
        vs_lead_id:   wanted_vs_lead,
        name:         snap.decision_maker_name  || "",
        title:        snap.decision_maker_title || "",
        email:        snap.decision_maker_email || wanted_email || "",
        company_name: snap.property_name || snap.company_name || "",
        city:         snap.city  || "",
        state:        snap.state || "",
        segment:      snap.segment || "",
        provider:     "vehicle_security",
        _hydrated_from_vs_lead: true,
      };
      applyVsDraft(synthetic, draft);
      clearVsDraft();
      setActiveTab("compose");
      if (synthetic.email) {
        toast.success(`Draft VS aplicado em ${synthetic.company_name || "contato"}`);
      } else {
        toast.info(
          `Draft VS aplicado em ${synthetic.company_name || "contato"} — ` +
          "sem email cadastrado, descubra pelo botão \"Find email\".");
      }
      return;
    }

    // 2b) Fallback — hydrate directly from the prospect doc so the composer
    //    still opens with full context (Iter220). Runs after the contacts
    //    fetch resolved and no match was found (either empty result or the
    //    prospect isn't in the currently-visible email bucket).
    if (wanted_pid && hydrated_pid_ref.current !== wanted_pid) {
      hydrated_pid_ref.current = wanted_pid;
      (async () => {
        try {
          const { data: p } = await axios.get(`${API}/smith/prospects/${wanted_pid}`);
          if (!p || !p.id) return;
          const dms = Array.isArray(p.decision_makers) ? p.decision_makers : [];
          // Prefer a DM matching the URL email, else the first DM with any
          // email, else the first DM overall, else a minimal skeleton.
          const dm =
            (wanted_email && dms.find((d) => (d.email || "").toLowerCase() === wanted_email)) ||
            dms.find((d) => d.email) ||
            dms[0] || {};
          const synthetic = {
            prospect_id:  p.id,
            name:         dm.name  || p.decision_maker_name  || "",
            title:        dm.title || p.decision_maker_title || "",
            email:        dm.email || wanted_email || "",
            company_name: p.company_name || p.name || "",
            city:         p.city  || (p.address && p.address.city)  || "",
            state:        p.state || (p.address && p.address.state) || "",
            segment:      p.segment || p.industry || "",
            provider:     p.provider || "",
            _hydrated_from_prospect: true,
          };
          if (draft) {
            applyVsDraft(synthetic, draft);
            clearVsDraft();
            setActiveTab("compose");
            toast.success(`Draft VS aplicado em ${synthetic.company_name || "contato"}`);
          } else {
            pickContact(synthetic);
            setActiveTab("compose");
            if (synthetic.email) {
              toast.success(`${synthetic.company_name || "Contato"} pré-carregado — Claire está redigindo`);
            } else {
              toast.info(
                `${synthetic.company_name || "Prospect"} carregado — sem email cadastrado ainda, ` +
                "descubra pelo botão \"Find email\" no Compose.");
            }
          }
        } catch (e) {
          toast.error(friendlyError(e, "Não consegui carregar esse prospect — abra pelo dossier."));
        }
      })();
    }
  }, [searchParams, contacts, loadingContacts]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Iter260 · Deep-link from Vision Analyzer:
  //   /smith/outreach?tab=retry&prefill_vision=1&prospect_id=<pid>[&send_id=<sid>][&analysis_id=<aid>]
  // Auto-runs `prefillRetryFromVision`, which:
  //   • pops the retry drawer with the freshly generated cold email, OR
  //   • opens the diff/preview modal when the target send already has body_snapshot.
  const vision_prefill_ref = useRef(null);
  useEffect(() => {
    const wanted = searchParams.get("prefill_vision");
    if (wanted !== "1") return;
    const pid = searchParams.get("prospect_id") || "";
    const sid = searchParams.get("send_id") || "";
    const aid = searchParams.get("analysis_id") || "";
    const key = `${pid}|${sid}|${aid}`;
    if (vision_prefill_ref.current === key) return;
    if (!pid && !sid && !aid) return;
    vision_prefill_ref.current = key;
    setActiveTab("retry");
    prefillRetryFromVision({
      prospect_id: pid || undefined,
      send_id: sid || undefined,
      analysis_id: aid || undefined,
    });
  }, [searchParams]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Iter203 — AI Search intelligence injection.
  // Deep-link from AI Search dossier: /smith/outreach?tab=compose&ai_company_id=...
  useEffect(() => {
    const wanted = searchParams.get("ai_company_id");
    if (!wanted) { setAiCompanyId(null); setAiBrief(null); return; }
    if (wanted === aiCompanyId && aiBrief) return;
    setAiCompanyId(wanted);
    setActiveTab("compose");
    (async () => {
      try {
        const r = await axios.get(`${API}/ai-search/companies/${wanted}/outreach-context`);
        setAiBrief(r.data?.brief || null);
        if (r.data?.brief) {
          toast.success(`AI Search intel carregada: ${r.data.brief.company_name}`);
        }
      } catch (ex) {
        toast.error("AI Search brief indisponível — email irá sem contexto verificado");
        setAiBrief(null);
      }
    })();
  }, [searchParams]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Templates: initial load + CRUD (Iter189)
  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/outreach/templates`);
      setTemplates(data.items || []);
    } catch (e) {
      // silent — templates are optional
    }
  };
  useEffect(() => { loadTemplates(); }, []);

  // ── Delivery status tracking (Iter192) — polls every 45s so opens/clicks
  // update in near-real-time without hammering the backend.
  const loadDeliveryStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/outreach/delivery-status`);
      setDeliveryMap(data.items || {});
    } catch (_) { /* silent */ }
  };
  useEffect(() => {
    loadDeliveryStatus();
    const iv = setInterval(loadDeliveryStatus, 45000);
    return () => clearInterval(iv);
  }, []);

  // ── Reply Inbox (Iter193) — polls unread + list.
  const loadReplies = async () => {
    try {
      const [{ data: list }, { data: count }] = await Promise.all([
        axios.get(`${API}/smith/outreach/replies?limit=50`),
        axios.get(`${API}/smith/outreach/replies/unread-count`),
      ]);
      setReplies(list.items || []);
      setUnreadReplies(count.unread || 0);
    } catch (_) { /* silent */ }
  };
  useEffect(() => {
    loadReplies();
    const iv = setInterval(loadReplies, 60000);
    return () => clearInterval(iv);
  }, []);

  // ── Auto-Nudge config quick-load (Iter195) — surfaces the green "active"
  // dot in the header even before the operator opens the modal.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smith/outreach/auto-nudge/config`);
        setNudgeCfg(data.config);
      } catch (_) { /* silent */ }
    })();
  }, []);

  // ── Iter198 — fetch analytics breakdown lazily when user opens the tab
  useEffect(() => {
    if (activeTab !== "analytics" || breakdown !== null) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smith/outreach/analytics/breakdown?days=180`);
        setBreakdown(data);
      } catch (_) { /* silent — panel handles null gracefully */ }
    })();
  }, [activeTab]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Iter235 · fetch signature analytics on the same trigger.
  useEffect(() => {
    if (activeTab !== "analytics" || sigAnalytics !== null) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smith/outreach/analytics/signature?days=180`);
        setSigAnalytics(data);
      } catch (_) { /* silent */ }
    })();
  }, [activeTab]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Iter200 — load sequences once so the Enroll button dropdown has data
  useEffect(() => { loadSequences(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const openReply = async (r) => {
    try {
      const { data } = await axios.get(`${API}/smith/outreach/replies/${r.id}`);
      setReplyBody(data);
      if (!r.read) {
        await axios.post(`${API}/smith/outreach/replies/${r.id}/mark-read`);
        setReplies((cur) => cur.map((x) => x.id === r.id ? { ...x, read: true } : x));
        setUnreadReplies((n) => Math.max(0, n - 1));
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao abrir resposta"));
    }
  };

  const replyToLead = (r) => {
    // Find the contact in the list and pick it — auto-fills recipient
    // and opens the compose flow so BD can send a follow-up in 1 click.
    const match = contacts.find(
      (c) => (c.email || "").toLowerCase() === (r.from_email || "").toLowerCase()
        || (c.prospect_id && c.prospect_id === r.prospect_id));
    if (match) {
      setRepliesOpen(false);
      setReplyBody(null);
      pickContact(match);
    } else {
      toast.info("Contato não encontrado na lista atual — abra em Contacts");
    }
  };

  // ── Sender Setup (Iter194) — DNS records + verification polling
  const openSenderSetup = async () => {
    setSenderOpen(true);
    setSenderBusy(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/sender-status`);
      setSenderStatus(data);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao buscar status do domínio"));
    } finally { setSenderBusy(false); }
  };

  const registerDomain = async () => {
    setSenderBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/sender-register`);
      toast.success(data.already_existed
        ? "Domínio já registrado no Resend"
        : "Domínio criado no Resend — adicione os 3 registros DNS abaixo");
      // Re-fetch status to pull the records + status.
      const { data: st } = await axios.get(`${API}/smith/outreach/sender-status`);
      setSenderStatus(st);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao registrar domínio"));
    } finally { setSenderBusy(false); }
  };

  const verifyDomain = async () => {
    setSenderBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/sender-verify`);
      setSenderStatus((cur) => ({ ...(cur || {}),
        resend_status: data.status,
        verified: data.verified,
        records: data.records }));
      if (data.verified) {
        toast.success("Domínio verificado! Peça pro time redeployar SMITH_SENDER_VERIFIED=1");
      } else {
        toast.info(`Ainda pendente (status: ${data.status}). DNS pode levar até 30 min.`);
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha na verificação"));
    } finally { setSenderBusy(false); }
  };

  const copyToClipboard = async (text, label = "Copiado") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("Clipboard bloqueado — copie manualmente");
    }
  };

  // ── Auto-Nudge SMS 48h (Iter195) — config + preview + manual run
  const openNudgeModal = async () => {
    setNudgeOpen(true);
    setNudgeBusy(true);
    try {
      const [{ data: cfg }, { data: pending }, { data: hist }] = await Promise.all([
        axios.get(`${API}/smith/outreach/auto-nudge/config`),
        axios.get(`${API}/smith/outreach/auto-nudge/pending?limit=25`),
        axios.get(`${API}/smith/outreach/auto-nudge/history?limit=10`),
      ]);
      setNudgeCfg(cfg.config);
      setNudgeCfgDraft(cfg.config);
      setNudgePending(pending);
      setNudgeHistory(hist);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao carregar Auto-Nudge"));
    } finally { setNudgeBusy(false); }
  };

  const saveNudgeCfg = async (patch) => {
    setNudgeBusy(true);
    try {
      const { data } = await axios.put(`${API}/smith/outreach/auto-nudge/config`, patch);
      setNudgeCfg(data.config);
      setNudgeCfgDraft(data.config);
      toast.success("Configuração salva");
      // Re-preview since window/template changed
      const { data: pending } = await axios.get(`${API}/smith/outreach/auto-nudge/pending?limit=25`);
      setNudgePending(pending);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao salvar configuração"));
    } finally { setNudgeBusy(false); }
  };

  const runNudgeNow = async () => {
    if (!confirm("Disparar Auto-Nudge SMS agora? Vai enviar SMS reais via Twilio para os candidatos elegíveis.")) return;
    setNudgeBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/auto-nudge/run-now`);
      if (data.skipped) {
        toast.info(`Pulado: ${data.reason}`);
      } else {
        toast.success(`Auto-Nudge executado · ${data.sent}/${data.picked} SMS enviados`);
      }
      // Refresh
      const [{ data: pending }, { data: hist }] = await Promise.all([
        axios.get(`${API}/smith/outreach/auto-nudge/pending?limit=25`),
        axios.get(`${API}/smith/outreach/auto-nudge/history?limit=10`),
      ]);
      setNudgePending(pending);
      setNudgeHistory(hist);
    } catch (e) {
      toast.error(friendlyError(e, "Falha na execução manual"));
    } finally { setNudgeBusy(false); }
  };

  // ── Bulk send helpers (Iter192)
  const toggleSelected = (pid) => setSelectedIds((cur) => {
    const next = new Set(cur);
    next.has(pid) ? next.delete(pid) : next.add(pid);
    return next;
  });
  const clearSelected = () => setSelectedIds(new Set());
  const selectAllVisible = () => {
    setSelectedIds(new Set(contacts.map((c) => c.prospect_id).filter(Boolean)));
  };

  const runBulkSend = async () => {
    // Iter208 + Iter224 — dispatch only rows with email that aren't
    // blacklisted. Toasts stay honest about what got skipped and why.
    const filtered = bulkFilteredContacts.filter(
      (c) => c.email && !blacklistedSet.has((c.email || "").toLowerCase()));
    const picked = filtered.length > 0 ? filtered
      : contacts.filter((c) => selectedIds.has(c.prospect_id) && c.email
                                 && !blacklistedSet.has((c.email || "").toLowerCase()));
    if (picked.length === 0) {
      const s = bulkStats;
      if (s.selected === 0) {
        toast.error("Selecione ao menos 1 contato antes de disparar");
      } else if (s.no_email === s.selected) {
        toast.error(
          `Nenhum dos ${s.selected} contatos selecionados tem email cadastrado. ` +
          "Rode 'Find email' antes de disparar.");
      } else if (s.blacklisted === s.with_email) {
        toast.error(
          `Todos os ${s.with_email} contatos com email estão bloqueados ` +
          "(unsubscribe / hard bounce). Remova o bloqueio ou selecione outros.");
      } else {
        toast.error("Nenhum contato válido — verifique filtros ativos");
      }
      return;
    }
    if (picked.length > 25) { toast.error("Máximo 25 contatos por lote"); return; }
    // Info toast when we're silently dropping rows
    const dropped = bulkStats.selected - picked.length;
    if (dropped > 0) {
      toast.info(
        `${dropped} contato${dropped === 1 ? "" : "s"} ignorado${dropped === 1 ? "" : "s"} ` +
        `(sem email ou bloqueados) — disparando ${picked.length}.`);
    }
    setBulkRunning(true);
    setBulkProgress({ total: picked.length, counts: { queued: picked.length, sending: 0, sent: 0, failed: 0, skipped: 0 }, items: [] });
    try {
      const { data } = await axios.post(`${API}/smith/outreach/bulk-send`, {
        contacts: picked.map((c) => ({
          prospect_id: c.prospect_id,
          to_email: c.email,
          name: c.name,
          title: c.title,
          company: c.company_name,
          city: c.city,
          state: c.state,
          segment: c.segment,
          provider: c.provider,
        })),
        tone, language,
        use_claire: !bulkTemplateId,        // template overrides Claire
        template_id: bulkTemplateId || undefined,
        attachments,
      }, { timeout: 20000 });
      const batchId = data.batch_id;
      toast.success(`Lote iniciado — ${picked.length} contatos em fila`);
      // Poll status every 2s until completed (max 4 min).
      const deadline = Date.now() + 240000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: st } = await axios.get(
            `${API}/smith/outreach/bulk-send/status/${batchId}`,
            { timeout: 10000 });
          setBulkProgress(st);
          if (st.status === "completed") break;
        } catch (_) { /* keep polling */ }
      }
      toast.success("Lote concluído");
      clearSelected();
      loadContacts();
      loadDeliveryStatus();
    } catch (e) {
      toast.error(friendlyError(e, "Falha no envio em lote"));
    } finally { setBulkRunning(false); }
  };

  const openSaveModal = () => {
    if (!subject.trim() || !(editorRef.current?.innerText || "").trim()) {
      toast.error("Preencha assunto e corpo antes de salvar");
      return;
    }
    setSaveName("");
    setSaveModalOpen(true);
  };

  const saveTemplate = async () => {
    const name = saveName.trim();
    if (name.length < 2) { toast.error("Nome do template muito curto"); return; }
    setSavingTemplate(true);
    try {
      // Persist HTML (contentEditable innerHTML) so bold/italic/list survive.
      const body = editorRef.current?.innerHTML || "";
      await axios.post(`${API}/smith/outreach/templates`, {
        name, subject: subject.trim(), body, tone, language,
      });
      toast.success(`Template "${name}" salvo`);
      setSaveModalOpen(false);
      await loadTemplates();
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao salvar template"));
    } finally { setSavingTemplate(false); }
  };

  const applyTemplate = async (tpl) => {
    setTemplatesMenuOpen(false);
    try {
      const { data } = await axios.post(
        `${API}/smith/outreach/templates/${tpl.id}/apply`,
        {
          name: selected?.name || null,
          title: selected?.title || null,
          company: selected?.company_name || null,
          city: selected?.city || null,
          state: selected?.state || null,
        },
      );
      setSubject(data.subject || "");
      if (editorRef.current) editorRef.current.innerHTML = data.body || "";
      if (data.tone) setTone(data.tone);
      if (data.language) setLanguage(data.language);
      setChanges([]);
      // Iter198 — remember the template used for analytics attribution
      setAppliedTemplateId(tpl.id);
      setAppliedTemplateName(tpl.name);
      toast.success(`Template "${tpl.name}" aplicado`);
      // Refresh use_count in the local list without a round-trip.
      setTemplates((cur) => cur.map((t) => t.id === tpl.id
        ? { ...t, use_count: (t.use_count || 0) + 1,
             last_used_at: new Date().toISOString() } : t));
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao aplicar template"));
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Excluir o template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/smith/outreach/templates/${tpl.id}`);
      setTemplates((cur) => cur.filter((t) => t.id !== tpl.id));
      toast.success("Template excluído");
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao excluir"));
    }
  };

  // ── When a contact is clicked, auto-generate the letter
  const pickContact = async (row) => {
    setSelected(row);
    setChanges([]);
    setSubject("");
    setAttachments([]);
    setLastSendError(null);
    // Iter198 — reset template attribution when contact changes
    setAppliedTemplateId(null);
    setAppliedTemplateName(null);
    if (editorRef.current) editorRef.current.innerText = "";
    // Reset override counter every time we switch prospect.
    langOverrideCountsRef.current = {};
    // Iter318 · If we've previously persisted a preferred_lang for this
    // prospect, that trumps the heuristic. Fire-and-forget fetch — the
    // generate-letter call will use `draftLang` computed here even if the
    // fetch races, and the panel adopts the pref for follow-ups.
    let draftLang = language;
    let persisted = null;
    if (row?.prospect_id) {
      try {
        const { data } = await axios.get(
          `${API}/smith/outreach/lead-context/${row.prospect_id}`,
          { timeout: 6000 });
        persisted = data?.preferred_lang || null;
      } catch (_e) { /* non-fatal — heuristic will still run */ }
    }
    if (persisted && (persisted === "pt" || persisted === "en" || persisted === "es")) {
      if (persisted !== language) {
        setLanguage(persisted);
        draftLang = persisted;
      }
    } else {
      // Iter316 · Auto-suggest via state/name heuristic when no memory.
      const suggested = detectLeadLang(row);
      if (suggested && suggested !== language) {
        setLanguage(suggested);
        draftLang = suggested;
        toast.success(
          suggested === "pt" ? "Rascunho em PT (auto-detectado pelo lead)"
          : suggested === "es" ? "Borrador en ES (auto-detectado)"
          : "Draft in EN (auto-detected)"
        );
      }
    }
    await generateLetter(row, draftLang);
  };

  const generateLetter = async (row = selected, overrideLang = null) => {
    if (!row && !aiCompanyId) return;
    setGenerating(true);
    setChanges([]);
    try {
      const payload = {
        prospect_id: row?.prospect_id,
        ai_search_company_id: aiCompanyId,   // Iter203 — Phase 4 personalisation
        name: row?.name, title: row?.title, email: row?.email,
        company: row?.company_name || aiBrief?.company_name,
        city: row?.city, state: row?.state,
        segment: row?.segment, provider: row?.provider,
        language: overrideLang || language, tone,
      };
      const { data } = await axios.post(`${API}/smith/outreach/generate-letter`,
        payload, { timeout: 60000 });
      setSubject(data.subject || "");
      if (editorRef.current) editorRef.current.innerText = data.body || "";
      toast.success(aiCompanyId
        ? "Carta gerada com AI Search intelligence (gatilho verificado)"
        : "Carta gerada pela Claire");
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao gerar carta"));
    } finally { setGenerating(false); }
  };

  const runSpellcheck = async () => {
    const text = editorRef.current?.innerText || "";
    if (!text.trim()) { toast.error("Editor vazio — nada para revisar"); return; }
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/spellcheck`, {
        text, language,
      }, { timeout: 30000 });
      if (editorRef.current) editorRef.current.innerText = data.corrected || text;
      setChanges(data.changes || []);
      if ((data.change_count || 0) === 0) {
        toast.success("Texto sem erros ✨");
      } else {
        toast.success(`${data.change_count} correção${data.change_count > 1 ? "ões" : ""} aplicada${data.change_count > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha na revisão"));
    } finally { setChecking(false); }
  };

  // Translate current subject+body to a different language, preserving edits.
  const translateTo = async (target) => {
    if (target === language) return;
    // Iter318 · manual-override memory. Every time the rep flicks the
    // language for the same prospect, bump the counter. On the 2nd flick
    // we PUT preferred_lang so the next open bypasses the heuristic.
    if (selected?.prospect_id) {
      const pid = selected.prospect_id;
      const map = langOverrideCountsRef.current;
      map[pid] = (map[pid] || 0) + 1;
      if (map[pid] >= 2) {
        // fire-and-forget — do not block the translate UX
        axios.put(
          `${API}/smith/outreach/lead-preferences/${pid}/lang`,
          { lang: target },
          { timeout: 5000 },
        ).catch(() => { /* silent — try again on next flick */ });
      }
    }
    const body = editorRef.current?.innerHTML || "";
    if (!subject.trim() && !body.trim()) {
      // Nothing to translate — just switch the target language for future gen.
      setLanguage(target);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/translate`, {
        subject: subject.trim(),
        body,
        from_language: language,
        to_language: target,
      }, { timeout: 40000 });
      setSubject(data.subject || subject);
      if (editorRef.current) editorRef.current.innerHTML = data.body || body;
      setLanguage(target);
      setChanges([]);
      toast.success(`Traduzido para ${LANGUAGE_OPTIONS.find((l) => l.id === target)?.label}`);
    } catch (e) {
      toast.error(friendlyError(e, "Falha na tradução"));
    } finally { setTranslating(false); }
  };

  // Upload a file → returns storage_path we track locally + include in send.
  const handleAttachmentPick = async (evt) => {
    const files = Array.from(evt.target?.files || []);
    evt.target.value = "";
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name}: excede 15 MB`); continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(
          `${API}/smith/outreach/attachments/upload`,
          fd,
          { timeout: 60000, headers: { "Content-Type": "multipart/form-data" } },
        );
        setAttachments((cur) => [...cur, {
          storage_path: data.storage_path,
          filename: data.filename,
          mime_type: data.mime_type,
          size_bytes: data.size_bytes,
        }]);
        toast.success(`${data.filename} anexado`);
      }
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao anexar"));
    } finally { setUploadingAttachment(false); }
  };

  const removeAttachment = (idx) => {
    setAttachments((cur) => cur.filter((_, i) => i !== idx));
  };

  const send = async () => {
    if (!selected?.email) { toast.error("Sem destinatário"); return; }
    const html = editorRef.current?.innerHTML || "";
    const plain = editorRef.current?.innerText || "";
    if (!subject.trim()) { toast.error("Preencha o assunto"); return; }
    if (!plain.trim()) { toast.error("Corpo do email vazio"); return; }
    setSending(true);
    setLastSendError(null);
    // Client-side idempotency key — used by both send-direct AND send-safe
    // so the bypass path replays the same result on retry.
    const idemKey = `${selected.prospect_id || "anon"}-${btoa(unescape(encodeURIComponent(subject))).slice(0, 32)}-${Math.floor(Date.now() / 60000)}`;
    const payload = {
      prospect_id: selected.prospect_id,
      to_email: selected.email,
      subject: subject.trim(),
      body: html,
      attachments,
      idempotency_key: idemKey,
      // Iter198 — attribution for template/tone analytics
      tone,
      template_id: appliedTemplateId,
      template_name: appliedTemplateName,
      // Iter230 · Language hint so the backend picks the right signature variant
      language,
    };

    // ── Try the direct (synchronous) path first ─────────────────────
    try {
      const { data } = await axios.post(`${API}/smith/outreach/send-direct`,
        payload, { timeout: 45000 });
      const replay = data.idempotent_replay;
      toast.success(replay
        ? "Envio já registrado — não duplicamos"
        : `E-mail enviado com sucesso para ${selected.email}`);
      setSentMap((m) => ({ ...m, [selected.prospect_id]: new Date().toISOString() }));
      setAttachments([]);
      loadContacts();
      return;
    } catch (e) {
      // Any error → try the bypass path automatically. This covers
      // Cloudflare 404/502/503/504, ingress timeouts, and network drops.
      // The bypass endpoint is a quick-ack that runs Resend in a background
      // task — it can't be intercepted by the gateway the same way.
      const status = e?.response?.status;
      const retryable = !status || status === 404 || status === 502
                        || status === 503 || status === 504
                        || e?.code === "ECONNABORTED" || e?.code === "ERR_NETWORK";
      if (!retryable) {
        const msg = friendlyError(e, "Falha no envio");
        setLastSendError(msg);
        toast.error(msg);
        setSending(false);
        return;
      }
      console.warn("[outreach] send-direct failed (status=" + status
                    + ") — falling back to send-safe", e);
      toast.info("Recuperando via caminho alternativo...");
    }

    // ── Bypass path ─────────────────────────────────────────────────
    try {
      const { data } = await axios.post(`${API}/smith/outreach/send-safe`,
        payload, { timeout: 15000 });
      if (data.idempotent_replay && data.status === "sent") {
        toast.success("Envio já registrado — não duplicamos");
        setSentMap((m) => ({ ...m, [selected.prospect_id]: new Date().toISOString() }));
        setAttachments([]);
        loadContacts();
        return;
      }
      // Queued — poll for status up to 30 s.
      toast.info("Enviando em segundo plano...");
      const key = data.idempotency_key;
      const deadline = Date.now() + 30000;
      let finalStatus = "queued";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data: st } = await axios.get(
            `${API}/smith/outreach/send-status/${encodeURIComponent(key)}`,
            { timeout: 8000 });
          if (st.status === "sent") { finalStatus = "sent"; break; }
          if (st.status === "failed") {
            finalStatus = "failed";
            setLastSendError(st.error || "Envio falhou no backend");
            break;
          }
        } catch (_pollErr) { /* keep polling — transient */ }
      }
      if (finalStatus === "sent") {
        toast.success(`E-mail enviado com sucesso para ${selected.email}`);
        setSentMap((m) => ({ ...m, [selected.prospect_id]: new Date().toISOString() }));
        setAttachments([]);
        loadContacts();
      } else if (finalStatus === "failed") {
        toast.error(lastSendError || "Envio falhou no backend");
      } else {
        toast.info("Envio ainda em processamento — acompanhe a lista em segundos");
        // Optimistically mark as sent since the server did receive+queue it.
        setSentMap((m) => ({ ...m, [selected.prospect_id]: new Date().toISOString() }));
        setAttachments([]);
      }
    } catch (e2) {
      const msg = friendlyError(e2, "Falha no envio (caminho alternativo)");
      setLastSendError(msg);
      toast.error(msg);
    } finally { setSending(false); }
  };

  // ── Rich-text toolbar (contentEditable + execCommand — good enough for MVP)
  const exec = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false);
  };

  const filteredCount = contacts.length;

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-outreach-header"
          title={t("so.header.title")}
          subtitle={t("so.header.subtitle", { n: contactsTotal.toLocaleString() })}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Send className="w-5 h-5" />
            </div>
          }
        >
          <HelpButton tutorialId="outreach" />
          <button type="button" onClick={() => setRepliesOpen(true)}
            data-testid="outreach-replies-btn"
            className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:border-[#3B82F6] hover:text-[#3B82F6] inline-flex items-center gap-1.5 relative">
            <Mail className="w-3.5 h-3.5" /> {t("so.btn.replies")}
            {unreadReplies > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#3B82F6] text-white text-[10px] font-bold tabular-nums"
                    data-testid="outreach-replies-badge">
                {unreadReplies}
              </span>
            )}
          </button>
          <button type="button" onClick={openSenderSetup}
            data-testid="outreach-sender-setup-btn"
            title={t("so.btn.sender_title")}
            className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:border-[#C9A24E] hover:text-[#C9A24E] inline-flex items-center gap-1.5">
            <Settings className="w-3.5 h-3.5" /> {t("so.btn.sender")}
          </button>
          <button type="button" onClick={openNudgeModal}
            data-testid="outreach-auto-nudge-btn"
            title={t("so.btn.nudge_title")}
            className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:border-[#F59E0B] hover:text-[#F59E0B] inline-flex items-center gap-1.5 relative">
            <Zap className="w-3.5 h-3.5" /> {t("so.btn.nudge")}
            {nudgeCfg?.enabled && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] absolute top-1.5 right-1.5"
                    data-testid="outreach-auto-nudge-active-dot" />
            )}
          </button>
          <button type="button" onClick={() => setDedupeOpen(true)}
            data-testid="outreach-dedupe-btn"
            title={t("so.btn.dedupe_title")}
            className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:border-[#8B5CF6] hover:text-[#8B5CF6] inline-flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> {t("so.btn.dedupe")}
          </button>
          <button type="button" onClick={loadContacts} disabled={loadingContacts}
            data-testid="outreach-refresh"
            className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:border-[#DC2626] hover:text-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loadingContacts ? "animate-spin" : ""}`} /> {t("so.btn.refresh")}
          </button>
        </AppHeader>

        {/* Iter249 · Outreach tab-bar */}
        <div className="max-w-[1700px] mx-auto px-3 sm:px-6 lg:px-8 pt-3">
          <OutreachTabBar current="outreach" />
        </div>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1700px] mx-auto pb-24 lg:pb-6"
              data-testid="smith-outreach-page">
          {/* Sub-tabs (Iter197) — unified email hub navigation */}
          <div className="flex items-center gap-1 mb-4 border-b border-white/10 overflow-x-auto"
               data-testid="outreach-subtabs">
            {[
              { id: "compose",   label: t("so.tab.compose"),   icon: Wand2 },
              { id: "drafts",    label: t("so.tab.drafts"),    icon: Sparkles, badge: draftsPending },
              { id: "contacts",  label: t("so.tab.contacts"),  icon: Users },
              { id: "templates", label: t("so.tab.templates"), icon: LibraryBig, badge: templates.length },
              { id: "sent",      label: t("so.tab.sent"),      icon: MailCheck },
              { id: "retry",     label: t("so.tab.retry"),     icon: RotateCcw, badge: failedCount },
              { id: "analytics", label: t("so.tab.analytics"), icon: BarChart3 },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button key={tab.id} type="button"
                        data-testid={`outreach-subtab-${tab.id}`}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 py-2 text-[12.5px] font-extrabold inline-flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${active ? "border-[#DC2626] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                  {tab.badge > 0 && (
                    // Iter232 · Retry Queue badge pulses red so operators
                    // notice failed sends without opening the tab.
                    <span data-testid={`outreach-subtab-badge-${tab.id}`}
                      className={"text-[10px] px-1.5 py-0.5 rounded font-black tabular-nums " +
                        (tab.id === "retry"
                          ? "bg-red-500/30 text-red-100 animate-pulse ring-1 ring-red-500/60"
                          : "bg-[#DC2626]/25 text-[#FCA5A5]")}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Iter254 · Import Hub trigger — always visible on the right */}
            <div className="ml-auto pl-2">
              <button type="button"
                onClick={() => setImportHubOpen(true)}
                data-testid="outreach-imports-btn"
                title={t("so.btn.imports_title")}
                className="h-8 px-3 rounded-lg text-[11.5px] font-black text-slate-200 border border-white/15 hover:border-[#DC2626] hover:text-white inline-flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" /> {t("so.btn.imports")}
              </button>
            </div>
          </div>

          {activeTab === "compose" && (
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
            {aiBrief && (
              <div className="lg:col-span-2 rounded-2xl border border-purple-500/30 p-4 space-y-2"
                   style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.10) 0%,rgba(15,23,42,0.4) 100%)" }}
                   data-testid="outreach-ai-brief-banner">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-purple-300 font-bold">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Search Intelligence · {aiBrief.company_name}
                    <span className="text-[10px] text-slate-400 normal-case tracking-normal">
                      · {aiBrief.opportunity_tier} {aiBrief.opportunity_score} · confidence {aiBrief.confidence_band?.replace("_"," ")}
                    </span>
                  </div>
                  <button type="button"
                    onClick={() => { setAiCompanyId(null); setAiBrief(null); }}
                    data-testid="outreach-ai-brief-clear"
                    className="text-[10px] text-slate-500 hover:text-white">✕ Ignorar contexto</button>
                </div>
                {aiBrief.prime_trigger && (
                  <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-2.5 text-[12px]"
                       data-testid="outreach-ai-brief-prime-trigger">
                    <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-0.5">
                      🔥 Gatilho principal
                    </div>
                    <div className="text-white">{aiBrief.prime_trigger.title}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {aiBrief.prime_trigger.type} · {aiBrief.prime_trigger.date || "—"}
                      {" · "}
                      <a href={aiBrief.prime_trigger.source_url} target="_blank" rel="noreferrer"
                         className="text-sky-400 hover:text-sky-300">fonte</a>
                    </div>
                  </div>
                )}
                {aiBrief.top_contacts?.length > 0 && (
                  <div className="text-[11px] text-slate-300">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mr-1.5">Contatos:</span>
                    {aiBrief.top_contacts.slice(0,3).map((c, i) => (
                      <span key={i} className="mr-2">
                        <strong className="text-white">{c.full_name}</strong>
                        <span className="text-slate-400"> ({c.job_title})</span>
                      </span>
                    ))}
                  </div>
                )}
                {aiBrief.current_vendors?.length > 0 && (
                  <div className="text-[11px] text-slate-300">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mr-1.5">Vendor evidenciado:</span>
                    {aiBrief.current_vendors.map((v, i) => (
                      <span key={i} className="mr-2">
                        <strong className="text-white">{v.vendor}</strong>
                        <span className="text-slate-400"> ({v.status})</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-slate-500">
                  Ao gerar, a Claire vai citar apenas fatos verificados listados acima.
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => generateLetter()}
                    disabled={generating}
                    data-testid="outreach-ai-brief-generate"
                    className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                    {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Gerar carta com AI Search
                  </button>
                </div>
              </div>
            )}
            {/* ── Contacts pane ─────────────────────────────────────── */}
            <aside className="rounded-2xl border overflow-hidden self-start"
                   style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                   data-testid="outreach-contacts-panel">
              {/* Iter206 — Email bucket tabs (com email / sem email / repositório) */}
              <div className="flex border-b border-white/10 bg-black/20" data-testid="outreach-email-bucket-tabs">
                {[
                  { id: "available",    label: t("so.bucket.available"),    icon: "✉" },
                  { id: "missing",      label: t("so.bucket.missing"),      icon: "🔍" },
                  { id: "unresolvable", label: t("so.bucket.unresolvable"), icon: "📦" },
                ].map((b) => (
                  <button key={b.id} type="button"
                    onClick={() => { setEmailBucket(b.id); autoDiscoveredRef.current = false; }}
                    data-testid={`outreach-bucket-${b.id}`}
                    className={`flex-1 h-9 text-[11.5px] font-bold border-b-2 -mb-px transition-colors ${
                      emailBucket === b.id
                        ? "border-red-500 text-white bg-red-500/10"
                        : "border-transparent text-slate-400 hover:text-white"}`}>
                    {b.icon} {b.label}
                  </button>
                ))}
              </div>
              {emailBucket === "missing" && autoDiscoveryBusy && (
                <div className="px-3 py-2 text-[10.5px] text-purple-300 border-b border-white/5 bg-purple-500/5 flex items-center gap-2"
                     data-testid="outreach-auto-discovery-status">
                  <Loader2 className="w-3 h-3 animate-spin" /> {t("so.contacts.loading_discovery")}
                </div>
              )}
              {/* Iter246 · Bulk deep-search retry (3 attempts × 30s) */}
              {emailBucket === "missing" && (contactsTotal || 0) > 0 && (
                <div className="px-3 py-2 text-[10.5px] text-blue-200 border-b border-white/5 bg-blue-500/5 flex items-start gap-2">
                  <div className="flex-1">
                    Retentar em <b>todos os {contacts.filter((c) => !c.email).length}</b> sem email · 3 tentativas espaçadas em 30s (Apollo → Hunter → Apify → SunBiz).
                  </div>
                  <button type="button" onClick={retryDeepSearchBulk}
                          disabled={Object.keys(deepSearchProgress).length > 0}
                          data-testid="outreach-deep-search-retry-bulk"
                          className="shrink-0 h-6 px-2 rounded text-[10px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-40"
                          style={{ background: "linear-gradient(135deg,#3B82F6 0%,#1D4ED8 100%)" }}>
                    {Object.keys(deepSearchProgress).length > 0
                      ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Rodando {Object.keys(deepSearchProgress).length}…</>
                      : <><RefreshCw className="w-2.5 h-2.5" /> Retentar em bulk</>}
                  </button>
                </div>
              )}
              {emailBucket === "unresolvable" && (
                <div className="px-3 py-2 text-[10.5px] text-amber-300 border-b border-white/5 bg-amber-500/5 flex items-start gap-2">
                  <div className="flex-1">
                    Prospects que Claire tentou e não achou email público. Não aparecem no fluxo principal.
                  </div>
                  <button type="button" onClick={retryUnresolvable}
                          disabled={retryingRepo || (contactsTotal || 0) === 0}
                          data-testid="outreach-retry-repo-btn"
                          title="Rodar Apollo → Hunter → Apify novamente nos prospects do repositório"
                          className="shrink-0 h-6 px-2 rounded text-[10px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-40"
                          style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                    {retryingRepo
                      ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Retentando…</>
                      : <><RefreshCw className="w-2.5 h-2.5" /> Retentar</>}
                  </button>
                </div>
              )}
              {/* Filters */}
              <div className="p-3 border-b border-white/10 space-y-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                         placeholder={t("so.search_placeholder")}
                         data-testid="outreach-search"
                         className="w-full pl-8 pr-9 h-9 rounded-lg bg-black/30 border border-white/10 text-[12.5px] text-white focus:border-[#DC2626] outline-none" />
                  <button type="button" onClick={() => setFilterOpen(true)}
                    data-testid="outreach-open-filters"
                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded flex items-center justify-center ${
                      (filterCity || filterSegment || filterFlagged || filterSent)
                        ? "bg-purple-500/30 text-purple-200"
                        : "text-slate-400 hover:text-white"}`}
                    title={t("so.filter.advanced_title")}>
                    <SlidersHorizontal className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <select value={source} onChange={(e) => setSource(e.target.value)}
                          data-testid="outreach-source-filter"
                          className="flex-1 h-8 px-2 rounded-lg bg-black/30 border border-white/10 text-[11.5px]">
                    <option value="">{t("so.filter.source_any")}</option>
                    <option value="apollo">Apollo</option>
                    <option value="hunter">Hunter</option>
                    <option value="sunbiz">Sunbiz</option>
                    <option value="manual">Import</option>
                  </select>
                  <select value={tier} onChange={(e) => setTier(e.target.value)}
                          data-testid="outreach-tier-filter"
                          className="flex-1 h-8 px-2 rounded-lg bg-black/30 border border-white/10 text-[11.5px]">
                    <option value="">{t("so.filter.tier_any")}</option>
                    <option value="hot">{t("so.filter.tier_hot")}</option>
                    <option value="high">{t("so.filter.tier_high")}</option>
                    <option value="qualified">{t("so.filter.tier_qualified")}</option>
                    <option value="nurture">{t("so.filter.tier_nurture")}</option>
                  </select>
                </div>
                {/* Iter228 · Auto-filter no-email leads */}
                <label className="flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
                  <input type="checkbox"
                    checked={filterOnlyWithEmail}
                    onChange={(e) => setFilterOnlyWithEmail(e.target.checked)}
                    data-testid="outreach-filter-only-with-email"
                    className="accent-[#DC2626]" />
                  <span>{t("so.filter.hide_no_email")}</span>
                  <span className="ml-auto text-[9.5px] text-slate-500">
                    {t("so.filter.hide_no_email_hint")}
                  </span>
                </label>
                {/* Iter285 · "Só falhados" filter + batch cleanup */}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
                    <input type="checkbox"
                      checked={filterOnlyFailed}
                      onChange={(e) => setFilterOnlyFailed(e.target.checked)}
                      data-testid="outreach-filter-only-failed"
                      className="accent-amber-500" />
                    <span>{t("so.filter.only_failed")}</span>
                    <span className="text-[10px] font-black text-amber-300 tabular-nums" data-testid="outreach-failed-count">
                      ({failedContactsCount})
                    </span>
                  </label>
                  {failedContactsCount > 0 && (
                    <button type="button"
                            onClick={cleanupFailedContacts}
                            disabled={cleaningFailed}
                            data-testid="outreach-cleanup-failed-btn"
                            title={t("so.cleanup.title")}
                            className="ml-auto h-7 px-2.5 rounded-md font-black text-[10.5px] text-white inline-flex items-center gap-1 disabled:opacity-40"
                            style={{ background: "linear-gradient(135deg,#F97316 0%,#B45309 100%)" }}>
                      {cleaningFailed
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("so.cleanup.cleaning")}</>
                        : <><Trash2 className="w-3 h-3" /> {t("so.cleanup.clear_n", { n: failedContactsCount })}</>}
                    </button>
                  )}
                </div>
                {/* Iter296 · Autoclean nightly-cron widget — last run
                    summary + on-demand dry-run / live trigger. */}
                {autoclean && (
                  <div className="rounded-md border border-white/10 bg-black/20"
                       data-testid="outreach-autoclean-widget">
                    <button type="button"
                            onClick={() => setAutocleanOpen((v) => !v)}
                            data-testid="outreach-autoclean-toggle"
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[10.5px] font-bold text-slate-200 hover:bg-white/5 rounded-md">
                      <Clock className="w-3 h-3 text-sky-400" />
                      <span>{t("so.autoclean.title")}</span>
                      <span className="text-slate-500 font-normal">
                        {t("so.autoclean.next_run")}
                      </span>
                      <span className="ml-auto flex items-center gap-1.5">
                        {autoclean.preview?.deletable_count > 0 && (
                          <span className="text-[9.5px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest bg-amber-400/25 text-amber-200 border border-amber-400/40"
                                data-testid="outreach-autoclean-preview-count">
                            {autoclean.preview.deletable_count} {t("so.autoclean.eligible")}
                          </span>
                        )}
                        {autoclean.last?.last_summary?.completed_at && (
                          <span className="text-[9.5px] text-slate-500 font-normal">
                            {t("so.autoclean.last_run", { date: new Date(autoclean.last.last_summary.completed_at).toLocaleDateString(), n: autoclean.last.last_summary.deleted || 0 })}
                          </span>
                        )}
                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${autocleanOpen ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    {autocleanOpen && (
                      <div className="px-2.5 pb-2.5 space-y-2 border-t border-white/10 pt-2">
                        <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                          <div className="rounded bg-black/20 px-2 py-1.5">
                            <div className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">{t("so.autoclean.candidates")}</div>
                            <div className="text-white font-black tabular-nums text-[13px]"
                                 data-testid="outreach-autoclean-candidates">
                              {autoclean.preview?.candidates || 0}
                            </div>
                          </div>
                          <div className="rounded bg-black/20 px-2 py-1.5">
                            <div className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">{t("so.autoclean.eligible_label")}</div>
                            <div className="text-amber-300 font-black tabular-nums text-[13px]"
                                 data-testid="outreach-autoclean-eligible">
                              {autoclean.preview?.deletable_count || 0}
                            </div>
                          </div>
                          <div className="rounded bg-black/20 px-2 py-1.5">
                            <div className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">{t("so.autoclean.skipped")}</div>
                            <div className="text-slate-300 font-black tabular-nums text-[13px]">
                              {autoclean.preview?.skipped || 0}
                            </div>
                          </div>
                        </div>
                        <div className="text-[9.5px] text-slate-500 leading-relaxed">
                          {t("so.autoclean.criteria")}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button type="button"
                            onClick={() => runAutoclean(true)}
                            disabled={autocleanRunning}
                            data-testid="outreach-autoclean-dryrun-btn"
                            className="h-7 px-2.5 rounded text-[10.5px] font-black text-slate-100 border border-white/15 hover:border-sky-400 inline-flex items-center gap-1 disabled:opacity-40">
                            {autocleanRunning
                              ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("so.autoclean.running_dry")}</>
                              : <><Sparkles className="w-3 h-3 text-sky-300" /> {t("so.autoclean.dry_run")}</>}
                          </button>
                          <button type="button"
                            onClick={() => runAutoclean(false)}
                            disabled={autocleanRunning || (autoclean.preview?.deletable_count || 0) === 0}
                            data-testid="outreach-autoclean-run-btn"
                            title={t("so.autoclean.live_title")}
                            className="h-7 px-2.5 rounded text-[10.5px] font-black text-white inline-flex items-center gap-1 disabled:opacity-40"
                            style={{ background: "linear-gradient(135deg,#DC2626 0%,#991B1B 100%)" }}>
                            {autocleanRunning
                              ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("so.autoclean.running_live")}</>
                              : <><Trash2 className="w-3 h-3" /> {t("so.autoclean.run_live")}</>}
                          </button>
                          <button type="button"
                            onClick={loadAutocleanStatus}
                            className="ml-auto text-[10px] font-bold text-slate-400 hover:text-white"
                            data-testid="outreach-autoclean-refresh">
                            {t("so.autoclean.refresh")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Bulk action bar (Iter192) */}
                <div className="flex items-center gap-1.5 pt-1 border-t border-white/5"
                     data-testid="outreach-bulk-bar">
                  <button type="button" onClick={selectAllVisible}
                          data-testid="outreach-bulk-select-all"
                          className="text-[10.5px] font-bold text-slate-400 hover:text-white">
                    {t("so.bulk.select_visible")}
                  </button>
                  {selectedIds.size > 0 && (
                    <button type="button" onClick={clearSelected}
                            data-testid="outreach-bulk-clear"
                            className="text-[10.5px] font-bold text-slate-500 hover:text-red-400">
                      {t("so.bulk.clear")} ({selectedIds.size})
                    </button>
                  )}
                  <button type="button"
                          onClick={() => setBulkOpen(true)}
                          disabled={selectedIds.size === 0 || bulkRunning}
                          data-testid="outreach-bulk-open"
                          className="ml-auto h-7 px-3 rounded-md font-bold text-[11px] text-white inline-flex items-center gap-1 disabled:opacity-40"
                          style={{ background: selectedIds.size ? "linear-gradient(135deg,#10B981 0%,#065F46 100%)" : "rgba(255,255,255,0.05)" }}>
                    <Users className="w-3 h-3" />
                    {t("so.bulk.action")} {selectedIds.size ? `(${selectedIds.size})` : ""}
                  </button>
                </div>
              </div>

              {/* List */}
              <div className="max-h-[70vh] overflow-y-auto" data-testid="outreach-contacts-list">
                {loadingContacts ? (
                  <div className="p-6 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-[#DC2626]" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="p-6 text-center text-[12px] text-slate-500"
                       data-testid="outreach-contacts-empty">
                    {t("so.contacts.empty")}
                  </div>
                ) : (() => {
                  // Iter228 · Auto-filter leads without email (default ON).
                  // Iter285 · Overlay "Só falhados" filter when active.
                  let rendered = filterOnlyWithEmail
                    ? contacts.filter((c) => c.email && c.email.includes("@"))
                    : contacts;
                  if (filterOnlyFailed) {
                    rendered = rendered.filter(isFailedContact);
                  }
                  const hiddenCount = contacts.length - rendered.length;
                  return (
                    <>
                      {hiddenCount > 0 && (
                        <div data-testid="outreach-hidden-noemail-banner"
                             className="px-3 py-2 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap"
                             style={{ background: "rgba(59,130,246,0.08)" }}>
                          <span className="text-[10.5px] text-slate-300">
                            {t("so.hidden_noemail_banner", { n: hiddenCount })}
                          </span>
                          <div className="flex items-center gap-2">
                            <button type="button"
                              onClick={enrichMissingEmailsBulk}
                              disabled={enrichingBulk}
                              data-testid="outreach-enrich-noemail-btn"
                              title={t("so.enrich_turbo_title")}
                              className="h-6 px-2 rounded text-[10px] font-black text-white inline-flex items-center gap-1 disabled:opacity-40"
                              style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                              {enrichingBulk
                                ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> {t("so.attaching")}</>
                                : <><Zap className="w-2.5 h-2.5" /> {t("so.enrich_turbo")}</>}
                            </button>
                            <button type="button"
                              onClick={() => setFilterOnlyWithEmail(false)}
                              data-testid="outreach-show-noemail-btn"
                              className="text-[10px] font-bold text-sky-300 hover:text-sky-100 underline">
                              {t("so.enrich_hidden_show")}
                            </button>
                          </div>
                        </div>
                      )}
                      {rendered.map((c, i) => {
                  const active = selected?.email === c.email && selected?.prospect_id === c.prospect_id;
                  const sent = !!sentMap[c.prospect_id];
                  const delivery = deliveryMap[c.prospect_id];
                  const isSelected = selectedIds.has(c.prospect_id);
                  // Iter208 — Unverified email heuristic (badge amarelo)
                  const isUnverified = !!c.email && (
                    !c.email_verified &&
                    !["apollo", "hunter"].includes((c.source || "").toLowerCase())
                  );
                  const isBlacklisted = c.email && blacklistedSet.has(c.email.toLowerCase());
                  return (
                    <div key={`${c.prospect_id}-${c.email}-${i}`}
                      data-testid={`outreach-contact-row-${i}`}
                      className={`w-full px-3 py-2.5 border-b border-white/5 flex items-start gap-2 transition
                                  ${active ? "bg-[#DC2626]/15 border-l-2 border-l-[#DC2626]" : "hover:bg-white/5"}
                                  ${isSelected ? "bg-emerald-500/5" : ""}`}>
                      {/* Checkbox for bulk select */}
                      <input type="checkbox" checked={isSelected}
                             onChange={() => c.prospect_id && !isBlacklisted && toggleSelected(c.prospect_id)}
                             onClick={(e) => e.stopPropagation()}
                             disabled={!c.prospect_id || isBlacklisted}
                             title={isBlacklisted ? "Contato na blacklist — envios bloqueados" : ""}
                             data-testid={`outreach-contact-select-${i}`}
                             className="mt-2 accent-emerald-500 shrink-0 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" />
                      <button type="button" onClick={() => pickContact(c)}
                              data-testid={`outreach-contact-${i}`}
                              className="flex-1 min-w-0 text-left flex items-start gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
                             style={{ background: `${tierColor(c.tier)}22`, color: tierColor(c.tier) }}>
                          {(c.name || c.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] font-bold text-white truncate">
                            {c.name || <span className="text-slate-500 italic">(sem nome)</span>}
                          </div>
                          <div className="text-[10.5px] text-slate-400 truncate">{c.title || "—"}</div>
                          <div className="text-[10.5px] text-[#F87171] font-mono truncate mt-0.5">{c.email}</div>
                          <div className="text-[10px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
                            <Building2 className="w-2.5 h-2.5" />
                            {c.company_name || "—"} <span className="opacity-40">·</span>
                            <MapPin className="w-2.5 h-2.5" />
                            {c.city || "?"}, {c.state || "?"}
                          </div>
                        </div>
                      </button>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        {/* Iter246 · Deep search progress spinner */}
                        {deepSearchProgress[c.prospect_id] && (
                          <span data-testid={`outreach-deep-search-progress-${c.prospect_id}`}
                                  className="inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-200 border border-blue-400/40">
                            <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            Buscando… tentativa {deepSearchProgress[c.prospect_id].attempt}/{deepSearchProgress[c.prospect_id].max}
                          </span>
                        )}
                        {/* Iter224 — "No email" badge (blocks bulk send). */}
                        {!c.email && !deepSearchProgress[c.prospect_id] && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300 border border-slate-500/30"
                                title="Sem email cadastrado — este contato será ignorado no bulk send."
                                data-testid={`outreach-no-email-${i}`}>
                            <Ban className="w-2.5 h-2.5" /> Sem email
                          </span>
                        )}
                        {/* Iter246 — tracking + retry (only in "missing" tab) */}
                        {!c.email && emailBucket === "missing"
                            && !deepSearchProgress[c.prospect_id] && (
                          <>
                            {(c.email_search_attempts_count || 0) > 0 && (
                              <span data-testid={`outreach-deep-search-attempts-${c.prospect_id}`}
                                      className="inline-flex items-center gap-1 text-[9px] text-slate-400 tabular-nums"
                                      title={
                                        (c.email_search_last_attempt?.sources_tried || []).join(", ")
                                          + " · última: "
                                          + (c.email_search_last_attempt?.attempted_at || "").slice(0, 16).replace("T", " ")
                                      }>
                                {c.email_search_attempts_count}× ·{" "}
                                <span className="opacity-70">
                                  {(c.email_search_last_attempt?.sources_tried || []).slice(0, 2).join(", ")}
                                  {(c.email_search_last_attempt?.sources_tried || []).length > 2 && "…"}
                                </span>
                              </span>
                            )}
                            <button type="button"
                                      onClick={(e) => { e.stopPropagation(); retryDeepSearchOne(c.prospect_id); }}
                                      data-testid={`outreach-deep-search-retry-${c.prospect_id}`}
                                      className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-200 border border-blue-400/40 hover:bg-blue-500/25">
                              <RotateCcw className="w-2.5 h-2.5" /> Retentar
                            </button>
                          </>
                        )}
                        {/* Iter208 — Blacklist "blocked" badge (highest priority) */}
                        {isBlacklisted && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/25 text-red-300 border border-red-400/40"
                                title={t("so.badge.blocked_title")}
                                data-testid={`outreach-blacklisted-${i}`}>
                            <Ban className="w-2.5 h-2.5" /> {t("so.badge.blocked")}
                          </span>
                        )}
                        {/* Iter208 — Unverified email warning */}
                        {isUnverified && !isBlacklisted && (
                          <>
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 border border-amber-400/40"
                                  title={t("so.badge.unverified_title")}
                                  data-testid={`outreach-unverified-${i}`}>
                              <AlertTriangle className="w-2.5 h-2.5" /> {t("so.badge.unverified")}
                            </span>
                            {/* Iter292 · Inline delete — only for unverified
                                 leads that were NEVER sent successfully.
                                 One-click removal, no confirmation dialog
                                 (undo is handled at the cleanup-summary toast). */}
                            {!c.email_sent_at && !sentMap[c.prospect_id] && (
                              <button type="button"
                                onClick={(e) => { e.stopPropagation(); deleteContactRow(c); }}
                                data-testid={`outreach-delete-unverified-${i}`}
                                title={t("so.badge.delete_unverified")}
                                className="inline-flex items-center justify-center w-5 h-5 rounded bg-amber-500/10 hover:bg-red-500/25 text-amber-300 hover:text-red-200 border border-amber-400/30 hover:border-red-400/50 transition-colors">
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </>
                        )}
                        {/* Iter207 — Champion badge (2+ replies detected) */}
                        {c.is_champion && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-400/30 to-yellow-500/30 text-amber-200 border border-amber-400/40"
                                title={t("so.badge.champion_title", { date: c.champion_since ? new Date(c.champion_since).toLocaleDateString() : "" })}
                                data-testid={`outreach-champion-${i}`}>
                            <Star className="w-2.5 h-2.5 fill-current" /> {t("so.badge.champion")}
                          </span>
                        )}
                        {/* Iter206 — persistent "enviado" badge from smith_outreach_sends */}
                        {c.email_sent_at && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300"
                                title={`${t("so.badge.sent")}: ${new Date(c.email_sent_at).toLocaleString()}`}
                                data-testid={`outreach-email-sent-${i}`}>
                            <MailCheck className="w-2.5 h-2.5" /> {t("so.badge.sent")}
                          </span>
                        )}
                        {delivery ? (
                          <DeliveryChip status={delivery.status} testid={`outreach-delivery-${i}`} />
                        ) : sent && !c.email_sent_at && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300"
                                data-testid={`outreach-sent-badge-${i}`}>
                            <CheckCircle2 className="w-2.5 h-2.5" /> {t("so.badge.sent")}
                          </span>
                        )}
                        {/* Iter206 — flag toggle button */}
                        {c.email && (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); flagDm(c); }}
                            data-testid={`outreach-flag-${i}`}
                            title={c.flagged ? t("so.badge.flag_remove") : t("so.badge.flag_add")}
                            className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                              c.flagged
                                ? "bg-amber-500/30 text-amber-200"
                                : "text-slate-500 hover:text-amber-300 hover:bg-amber-500/10"}`}>
                            <Flag className={`w-2.5 h-2.5 ${c.flagged ? "fill-current" : ""}`} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                    </>
                  );
                })()}
              </div>
              <div className="px-3 py-2 border-t border-white/10 text-[10.5px] text-slate-500 text-center">
                {t("so.footer.count", { shown: filteredCount, total: contactsTotal.toLocaleString() })}
              </div>
            </aside>

            {/* ── Compose pane ──────────────────────────────────────── */}
            <section className="rounded-2xl border overflow-hidden flex flex-col"
                     style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)", minHeight: "72vh" }}
                     data-testid="outreach-compose-panel">
              {!selected ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6 py-16"
                     data-testid="outreach-empty-state">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                       style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-[16px] font-extrabold text-white">{t("so.empty.title")}</div>
                  <div className="text-[12.5px] text-slate-400 max-w-md">
                    {t("so.empty.body")}
                  </div>
                </div>
              ) : (
                <>
                  {/* Recipient bar */}
                  <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-3"
                       data-testid="outreach-recipient-bar">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold shrink-0">{t("so.recipient.to")}</span>
                      <span className="text-[13px] font-bold text-white truncate" data-testid="outreach-to-name">
                        {selected.name || selected.email || t("so.recipient.no_name")}
                      </span>
                      {selected.title && (
                        <span className="text-[11px] text-slate-400 truncate">· {selected.title}</span>
                      )}
                      {selected.email ? (
                        <span className="text-[11px] font-mono text-[#F87171] truncate" data-testid="outreach-to-email">
                          &lt;{selected.email}&gt;
                        </span>
                      ) : (
                        <button type="button" onClick={findEmailForSelected} disabled={findingEmail}
                                data-testid="outreach-find-email-btn"
                                title={t("so.recipient.find_email_title")}
                                className="h-7 px-2.5 rounded text-[11px] font-extrabold text-white inline-flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                          {findingEmail
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("so.recipient.finding")}</>
                            : <><Search className="w-3 h-3" /> {t("so.recipient.find_email")}</>}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={tone} onChange={(e) => setTone(e.target.value)}
                              data-testid="outreach-tone-select"
                              className="h-8 px-2 rounded-lg bg-black/30 border border-white/10 text-[11.5px]">
                        {TONE_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{t("so.tone.prefix")} · {t(opt.i18n)}</option>
                        ))}
                      </select>
                      {/* Language selector — click any flag to TRANSLATE current draft */}
                      <div className="inline-flex items-center rounded-lg border border-white/10 bg-black/30 overflow-hidden"
                           data-testid="outreach-language-group">
                        {LANGUAGE_OPTIONS.map((l) => (
                          <button key={l.id} type="button"
                            onClick={() => translateTo(l.id)}
                            disabled={translating || generating}
                            data-testid={`outreach-lang-${l.id}`}
                            title={l.id === language ? t("so.lang.current") : t("so.lang.translate_to", { label: l.label })}
                            className={`h-8 px-2 text-[11.5px] font-bold transition ${
                              l.id === language
                                ? "bg-[#DC2626] text-white"
                                : "text-slate-300 hover:bg-white/5"
                            }`}>
                            {translating && l.id !== language
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : l.label}
                          </button>
                        ))}
                      </div>
                      <button type="button" onClick={() => generateLetter()}
                              disabled={generating}
                              data-testid="outreach-regenerate"
                              className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
                        {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                        {generating ? t("so.regenerating") : t("so.regenerate")}
                      </button>
                      {/* Templates picker (Iter189) */}
                      <div className="relative">
                        <button type="button" onClick={() => setTemplatesMenuOpen((v) => !v)}
                                data-testid="outreach-templates-btn"
                                className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-white inline-flex items-center gap-1.5 border border-white/15 hover:border-[#DC2626]">
                          <LibraryBig className="w-3 h-3" />
                          {t("so.templates.title")}
                          {templates.length > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] tabular-nums">
                              {templates.length}
                            </span>
                          )}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {templatesMenuOpen && (
                          <div className="absolute z-40 right-0 mt-1 w-[320px] max-h-[420px] overflow-y-auto rounded-lg border border-white/10 bg-[#0F172A] shadow-2xl p-2"
                               data-testid="outreach-templates-menu">
                            {templates.length === 0 ? (
                              <div className="p-4 text-center text-[11px] text-slate-500"
                                   data-testid="outreach-templates-empty">
                                {t("so.templates.empty")}
                              </div>
                            ) : templates.map((tpl) => (
                              <div key={tpl.id}
                                   data-testid={`outreach-template-row-${tpl.id}`}
                                   className="group flex items-start gap-2 px-2 py-2 rounded-md hover:bg-white/5 border-b border-white/5 last:border-b-0">
                                <button type="button" onClick={() => applyTemplate(tpl)}
                                        data-testid={`outreach-template-apply-${tpl.id}`}
                                        className="flex-1 min-w-0 text-left">
                                  <div className="text-[12.5px] font-bold text-white truncate">{tpl.name}</div>
                                  <div className="text-[10.5px] text-slate-400 truncate">{tpl.subject}</div>
                                  <div className="text-[9.5px] text-slate-500 mt-0.5 tabular-nums inline-flex items-center gap-2">
                                    <span>{tpl.language === "pt" ? "🇧🇷 PT" : "🇺🇸 EN"}</span>
                                    <span>· {tpl.tone}</span>
                                    <span>· {tpl.use_count || 0} uso{(tpl.use_count || 0) === 1 ? "" : "s"}</span>
                                  </div>
                                </button>
                                <button type="button" onClick={() => deleteTemplate(tpl)}
                                        data-testid={`outreach-template-delete-${tpl.id}`}
                                        title={t("so.templates.delete")}
                                        className="opacity-0 group-hover:opacity-100 shrink-0 w-6 h-6 rounded flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={openSaveModal}
                              data-testid="outreach-save-template-btn"
                              title={t("so.save_template_title")}
                              className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-white inline-flex items-center gap-1.5 border border-white/15 hover:border-emerald-400">
                        <BookmarkPlus className="w-3 h-3" />
                        {t("so.save_template")}
                      </button>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="px-4 py-3 border-b border-white/10">
                    <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold block mb-1">
                      {t("so.subject.label")}
                    </label>
                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                           placeholder={t("so.subject.placeholder")}
                           data-testid="outreach-subject"
                           className="w-full h-9 px-3 rounded-lg bg-black/30 border border-white/10 text-[13px] text-white font-bold focus:border-[#DC2626] outline-none" />
                    <div className="text-[10px] text-slate-500 tabular-nums mt-1">
                      {t("so.subject.counter", { n: subject.length })}
                    </div>
                  </div>

                  {/* Toolbar */}
                  <div className="px-4 py-2 border-b border-white/10 flex flex-wrap items-center gap-1.5"
                       data-testid="outreach-toolbar">
                    <ToolbarBtn testid="tool-bold" onClick={() => exec("bold")} icon={Bold} title={t("so.tool.bold")} />
                    <ToolbarBtn testid="tool-italic" onClick={() => exec("italic")} icon={Italic} title={t("so.tool.italic")} />
                    <ToolbarBtn testid="tool-underline" onClick={() => exec("underline")} icon={Underline} title={t("so.tool.underline")} />
                    <ToolbarBtn testid="tool-list" onClick={() => exec("insertUnorderedList")} icon={List} title={t("so.tool.list")} />
                    <span className="mx-1 h-5 w-px bg-white/10" />
                    <button type="button" onClick={runSpellcheck} disabled={checking || generating}
                            data-testid="outreach-spellcheck-btn"
                            className="h-8 px-3 rounded-lg font-bold text-[11.5px] inline-flex items-center gap-1.5 disabled:opacity-50 text-white"
                            style={{ background: "linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)" }}>
                      {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <SpellCheck2 className="w-3 h-3" />}
                      {checking ? t("so.spellchecking") : t("so.spellcheck")}
                    </button>
                    <div className="ml-auto text-[10.5px] text-slate-500">
                      {t("so.toolbar_hint")}
                    </div>
                  </div>

                  {/* Editor */}
                  <div className="flex-1 flex flex-col min-h-[300px]">
                    <div ref={editorRef}
                         contentEditable={!generating}
                         suppressContentEditableWarning
                         data-testid="outreach-editor"
                         className="flex-1 px-5 py-4 text-[13.5px] leading-relaxed text-slate-100 outline-none overflow-y-auto whitespace-pre-wrap"
                         style={{ minHeight: 260, maxHeight: "45vh" }} />
                    {generating && (
                      <div className="px-5 py-2 text-[11.5px] text-purple-300 inline-flex items-center gap-2 border-t border-white/10">
                        <Loader2 className="w-3 h-3 animate-spin" /> {t("so.editor.generating", { tone: t(TONE_OPTIONS.find((opt) => opt.id === tone)?.i18n || "so.tone.consultative") })}
                      </div>
                    )}
                  </div>

                  {/* Spellcheck diff */}
                  {changes.length > 0 && (
                    <div className="px-4 py-2 border-t border-white/10 max-h-32 overflow-y-auto"
                         data-testid="outreach-changes-panel">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10.5px] uppercase tracking-widest font-bold text-blue-300">
                          {t("so.changes.title", { n: changes.length })}
                        </span>
                        <button type="button" onClick={() => setChanges([])}
                                data-testid="outreach-changes-dismiss"
                                className="text-slate-500 hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <ul className="space-y-0.5 text-[11px]">
                        {changes.map((ch, i) => (
                          <li key={i} data-testid={`outreach-change-${i}`}
                              className="flex items-center gap-1.5">
                            <span className="line-through text-red-300/80 font-mono truncate max-w-[35%]">{ch.original}</span>
                            <span className="text-slate-500">→</span>
                            <span className="text-emerald-300 font-mono truncate max-w-[35%]">{ch.corrected}</span>
                            {ch.reason && <span className="text-slate-500 italic text-[10px] truncate flex-1">· {ch.reason}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Attachments row (Iter190) */}
                  <div className="px-4 py-2 border-t border-white/10 flex flex-wrap items-center gap-2"
                       data-testid="outreach-attachments-row">
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingAttachment}
                            data-testid="outreach-attach-btn"
                            className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-slate-200 inline-flex items-center gap-1.5 border border-white/15 hover:border-[#C9A24E] disabled:opacity-50">
                      {uploadingAttachment
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Paperclip className="w-3 h-3" />}
                      {uploadingAttachment ? t("so.attaching") : t("so.attach")}
                    </button>
                    <input ref={fileInputRef} type="file" multiple
                           onChange={handleAttachmentPick}
                           data-testid="outreach-attach-input"
                           accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.heic,.txt,.csv"
                           style={{ display: "none" }} />
                    {attachments.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="outreach-attachments-list">
                        {attachments.map((a, i) => {
                          const isImg = (a.mime_type || "").startsWith("image/");
                          return (
                            <span key={i}
                                  data-testid={`outreach-attachment-${i}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[#C9A24E]/40 bg-[#C9A24E]/10 text-[11px]">
                              {isImg ? <ImageIcon className="w-3 h-3 text-[#C9A24E]" /> : <FileText className="w-3 h-3 text-[#C9A24E]" />}
                              <span className="text-slate-200 max-w-[180px] truncate">{a.filename}</span>
                              <span className="text-slate-500 tabular-nums text-[10px]">
                                {a.size_bytes ? `${(a.size_bytes / 1024).toFixed(0)}KB` : ""}
                              </span>
                              <button type="button" onClick={() => removeAttachment(i)}
                                      data-testid={`outreach-attachment-remove-${i}`}
                                      className="text-slate-500 hover:text-red-400">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-[10.5px] text-slate-500">
                        {t("so.attach_hint")}
                      </span>
                    )}
                    <button type="button" onClick={() => setSignatureOpen(true)}
                      data-testid="outreach-signature-edit-btn"
                      title={t("so.signature.title")}
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded hover:bg-white/10 border border-white/10 transition-colors">
                      <CheckCircle2 className={`w-3 h-3 ${signatureExists ? "text-emerald-400" : "text-slate-500"}`} />
                      <span className={signatureExists ? "text-emerald-300" : "text-slate-400"}>
                        {signatureExists ? t("so.signature.custom_active") : t("so.signature.edit")}
                      </span>
                    </button>
                  </div>

                  {/* Success / error banner (Iter190) */}
                  {sentMap[selected.prospect_id] && !lastSendError && (
                    <div className="mx-4 mb-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center gap-2"
                         data-testid="outreach-success-banner">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-[12px] text-emerald-200 font-bold">
                        {t("so.banner.sent_ok", { email: selected.email })}
                      </span>
                      <span className="text-[10.5px] text-emerald-300/80 ml-auto tabular-nums">
                        {new Date(sentMap[selected.prospect_id]).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                  )}
                  {lastSendError && (
                    <div className="mx-4 mb-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 flex items-start gap-2"
                         data-testid="outreach-error-banner">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <span className="text-[12px] text-red-200 flex-1">{lastSendError}</span>
                      <button type="button" onClick={() => { setLastSendError(null); send(); }}
                              disabled={sending}
                              data-testid="outreach-retry-btn"
                              className="text-[11px] font-bold text-red-100 underline hover:text-white">
                        {t("so.banner.retry")}
                      </button>
                    </div>
                  )}

                  {/* Send bar */}
                  <div className="px-4 py-3 border-t border-white/10 flex items-center justify-end gap-2 relative z-30"
                       data-testid="outreach-send-bar">
                    {sentMap[selected.prospect_id] && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded bg-emerald-500/20 text-emerald-300"
                            data-testid="outreach-last-sent">
                        <CheckCircle2 className="w-3 h-3" />
                        {t("so.send.last_sent", { at: new Date(sentMap[selected.prospect_id]).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) })}
                      </span>
                    )}
                    <div className="relative">
                      <button type="button" onClick={() => setEnrollOpen((v) => !v)}
                              disabled={!selected?.prospect_id || sequences.length === 0}
                              data-testid="outreach-enroll-btn"
                              title={sequences.length === 0 ? t("so.send.enroll_missing") : t("so.send.enroll_title")}
                              className="h-10 px-4 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
                        <ListChecks className="w-4 h-4" /> {t("so.send.enroll")}
                      </button>
                      {enrollOpen && (
                        <div className="absolute right-0 top-full mt-1 w-72 rounded-lg border border-white/10 shadow-xl z-30 max-h-72 overflow-y-auto"
                             style={{ background: "#0B1120" }}
                             data-testid="outreach-enroll-dropdown">
                          <div className="px-3 py-2 border-b border-white/10 text-[10.5px] font-bold uppercase tracking-widest text-slate-400">
                            {t("so.send.enroll_pick")}
                          </div>
                          {sequences.map((s, i) => (
                            <button key={s.id} type="button"
                                    onClick={() => enrollInSequence(s)}
                                    disabled={enrolling}
                                    data-testid={`outreach-enroll-seq-${i}`}
                                    className="w-full px-3 py-2 text-left hover:bg-white/5 border-b border-white/5 last:border-0 disabled:opacity-50">
                              <div className="text-[12px] font-bold text-white">{s.name}</div>
                              <div className="text-[10px] text-slate-400">
                                {s.steps.length} steps · {s.steps.map((st) => `D${st.delay_days}`).join(" → ")}
                              </div>
                            </button>
                          ))}
                          <Link to="/smith/sequences"
                                className="block px-3 py-2 text-[11px] font-bold text-[#DC2626] hover:bg-white/5 text-center">
                            {t("so.send.manage_cadences")}
                          </Link>
                        </div>
                      )}
                    </div>
                    <button type="button" onClick={send}
                            disabled={sending || generating || translating || uploadingAttachment || !subject.trim()}
                            data-testid="outreach-send-btn"
                            className="h-10 px-5 rounded-lg font-bold text-[13px] text-white inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {sending ? t("so.send.sending") : `${t("so.send.button")}${attachments.length ? ` · ${attachments.length}` : ""}`}
                    </button>
                  </div>
                </>
              )}
            </section>
          </div>
          )}

          {/* ── Contacts tab (Iter197) — unified email directory ─────── */}
          {activeTab === "contacts" && (
            <div className="rounded-2xl border overflow-hidden"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                 data-testid="outreach-tab-contacts">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <Users className="w-4 h-4 text-[#DC2626]" />
                <div className="flex-1">
                  <div className="text-[13px] font-extrabold text-white">{t("so.tab.contacts.title")}</div>
                  <div className="text-[11px] text-slate-400">
                    {t("so.tab.contacts.subtitle", { n: contactsTotal.toLocaleString() })}
                  </div>
                </div>
                <button type="button" onClick={runBulkFindEmail} disabled={bulkFindRunning}
                        data-testid="outreach-contacts-bulk-find-btn"
                        title={t("so.tab.contacts.bulk_find_title")}
                        className="h-8 px-3 rounded text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                  {bulkFindRunning
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("so.tab.contacts.bulk_processed", { n: bulkFindProgress.processed })}</>
                    : <><Search className="w-3 h-3" /> {t("so.tab.contacts.bulk_find")}</>}
                </button>
                <button type="button" onClick={() => setDedupeOpen(true)}
                        data-testid="outreach-tab-contacts-dedupe"
                        className="h-8 px-3 rounded text-[11.5px] font-bold text-white inline-flex items-center gap-1.5"
                        style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
                  <Trash2 className="w-3 h-3" /> {t("so.btn.dedupe")}
                </button>
              </div>
              {bulkFindRunning && (
                <div className="px-4 py-2 border-b border-white/10 bg-amber-500/10"
                     data-testid="outreach-bulk-find-progress">
                  <div className="text-[11px] text-amber-200 flex items-center justify-between">
                    <span>{t("so.tab.contacts.bulk_progress", { done: bulkFindProgress.processed, found: bulkFindProgress.newEmails })}</span>
                  </div>
                  <div className="h-1 bg-black/30 rounded mt-1.5 overflow-hidden">
                    <div className="h-full bg-amber-400 transition-all"
                         style={{ width: `${Math.min(100, (bulkFindProgress.processed / 68) * 100)}%` }} />
                  </div>
                </div>
              )}
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-white/5"
                   data-testid="outreach-contacts-tab-list">
                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-[12px] text-slate-500">
                    {t("so.tab.contacts.empty")}
                  </div>
                ) : contacts.slice(0, 200).map((c, i) => (
                  <div key={c.email + i}
                       data-testid={`outreach-contacts-tab-row-${i}`}
                       className="px-4 py-2 flex items-center gap-3 hover:bg-white/5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[12.5px] font-bold text-white truncate">{c.name || c.email}</span>
                        <span className="text-[10px] text-slate-400 truncate">{c.title || "—"}</span>
                      </div>
                      <div className="text-[10.5px] font-mono text-[#F87171] truncate">{c.email}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {c.company_name} · {c.city}/{c.state} · {c.source} · score {c.score || 0}
                      </div>
                    </div>
                    <button type="button" onClick={() => { pickContact(c); setActiveTab("compose"); }}
                            data-testid={`outreach-contacts-tab-compose-${i}`}
                            className="h-7 px-2.5 rounded text-[11px] font-bold text-white inline-flex items-center gap-1"
                            style={{ background: "linear-gradient(135deg,#10B981 0%,#065F46 100%)" }}>
                      <Wand2 className="w-3 h-3" /> {t("so.tab.contacts.compose")}
                    </button>
                  </div>
                ))}
                {contacts.length > 200 && (
                  <div className="px-4 py-2 text-[10.5px] text-slate-500 text-center">
                    {t("so.tab.contacts.showing_of", { total: contacts.length })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Templates tab (Iter197) ────────────────────────────── */}
          {activeTab === "templates" && (
            <div className="rounded-2xl border overflow-hidden"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                 data-testid="outreach-tab-templates">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <LibraryBig className="w-4 h-4 text-[#DC2626]" />
                <div className="flex-1 text-[13px] font-extrabold text-white">
                  Templates salvos ({templates.length})
                </div>
                <div className="text-[10.5px] text-slate-400">
                  Placeholders suportados: <span className="font-mono text-slate-300">{"{{first_name}} {{company}} {{city}}"}</span>
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[70vh] overflow-y-auto"
                   data-testid="outreach-templates-grid">
                {templates.length === 0 ? (
                  <div className="col-span-full p-6 text-center text-[12px] text-slate-500">
                    Nenhum template salvo. Depois de redigir uma carta, use "Salvar como template" na aba Compose.
                  </div>
                ) : templates.map((t, i) => (
                  <div key={t.id}
                       data-testid={`outreach-template-card-${i}`}
                       className="rounded-lg border border-white/10 p-3 space-y-2 hover:border-[#DC2626]/40 transition">
                    <div className="flex items-start gap-2">
                      <span className="text-[12.5px] font-extrabold text-white flex-1 line-clamp-2">{t.name}</span>
                      <button type="button" onClick={() => deleteTemplate(t)}
                              data-testid={`outreach-template-delete-${i}`}
                              title="Excluir template"
                              className="w-6 h-6 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 flex items-center justify-center shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="text-[10.5px] text-slate-400 line-clamp-1 font-mono">{t.subject}</div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                      <span className="px-1.5 py-0.5 rounded bg-slate-500/15">{t.tone}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-500/15">{t.language?.toUpperCase()}</span>
                      <span className="ml-auto">Uso: {t.use_count || 0}</span>
                    </div>
                    <button type="button" onClick={() => { applyTemplate(t); setActiveTab("compose"); }}
                            data-testid={`outreach-template-apply-${i}`}
                            disabled={!selected}
                            title={!selected ? "Selecione um contato na aba Compose antes" : ""}
                            className="w-full h-7 rounded text-[11px] font-bold text-white inline-flex items-center justify-center gap-1 disabled:opacity-40"
                            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                      <Wand2 className="w-3 h-3" /> Usar template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Iter206 — Filter drawer (backdrop) for the Compose contacts panel */}
          {activeTab === "compose" && filterOpen && (
            <div className="fixed inset-0 z-50 flex justify-end"
                 data-testid="outreach-filter-drawer"
                 onClick={() => setFilterOpen(false)}>
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <div className="relative w-full sm:w-[380px] max-h-full overflow-y-auto p-5 space-y-4 border-l border-white/10"
                   style={{ background: "#0F172A" }}
                   onClick={(e) => e.stopPropagation()}>
                <header className="flex items-center justify-between">
                  <h3 className="text-[14px] font-bold text-white flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-purple-400" /> Filtros
                  </h3>
                  <button type="button" onClick={() => setFilterOpen(false)}
                    data-testid="outreach-filter-close"
                    className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </header>

                <label className="block">
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Segmentação</div>
                  <select value={filterSegment} onChange={(e) => setFilterSegment(e.target.value)}
                    data-testid="outreach-filter-segment"
                    className="w-full h-9 px-2 rounded border border-white/15 bg-black/30 text-[12px] text-white">
                    <option value="">Todos segmentos</option>
                    <option value="warehouses">Warehouses</option>
                    <option value="auto_auctions">Auto Auctions</option>
                    <option value="hoas">HOAs / Condos</option>
                    <option value="construction">Construction Sites</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="data_centers">Data Centers</option>
                    <option value="logistics">Logistics / DC</option>
                    <option value="hospitality">Hospitality</option>
                  </select>
                </label>

                <label className="block">
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Cidade</div>
                  <input type="text" value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
                    data-testid="outreach-filter-city"
                    placeholder="Ex: Orlando, Miami, Tampa"
                    className="w-full h-9 px-2 rounded border border-white/15 bg-black/30 text-[12px] text-white" />
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={filterFlagged}
                    onChange={(e) => setFilterFlagged(e.target.checked)}
                    data-testid="outreach-filter-flagged"
                    className="accent-amber-500" />
                  <Flag className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-[12px] text-slate-200">Somente sinalizados</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={filterSent}
                    onChange={(e) => setFilterSent(e.target.checked)}
                    data-testid="outreach-filter-sent"
                    className="accent-emerald-500" />
                  <MailCheck className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="text-[12px] text-slate-200">Somente já enviados</span>
                </label>

                <div className="pt-3 border-t border-white/5 flex gap-2">
                  <button type="button"
                    onClick={() => {
                      setFilterCity(""); setFilterSegment("");
                      setFilterFlagged(false); setFilterSent(false);
                    }}
                    data-testid="outreach-filter-reset"
                    className="flex-1 h-9 rounded-lg border border-white/15 text-[12px] font-bold text-slate-300 hover:border-purple-500/60">
                    Limpar
                  </button>
                  <button type="button" onClick={() => setFilterOpen(false)}
                    data-testid="outreach-filter-apply"
                    className="flex-1 h-9 rounded-lg text-[12px] font-bold text-white"
                    style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                    Aplicar
                  </button>
                </div>

                <div className="text-[10.5px] text-slate-500 leading-relaxed pt-2 border-t border-white/5">
                  💡 Dica: os filtros aplicam sobre a aba ativa ({emailBucket === "available" ? "Com email" : emailBucket === "missing" ? "Sem email" : "Repositório"}).
                </div>
              </div>
            </div>
          )}

          {/* ── Sent tab (Iter197) — send activity table ───────────── */}
          {activeTab === "drafts" && (
            <div className="rounded-2xl border overflow-hidden"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(217,119,6,0.30)" }}
                 data-testid="outreach-tab-drafts">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <div className="flex-1 text-[13px] font-extrabold text-white">
                  Rascunhos automáticos ({drafts.length})
                </div>
                <div className="text-[10.5px] text-amber-300/70">
                  Sinais 90+ → Claire redige · limite 3/prospect/24h
                </div>
                <button type="button" onClick={loadDrafts}
                        data-testid="outreach-drafts-refresh"
                        className="h-7 px-2.5 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${draftsLoading?"animate-spin":""}`} /> Refresh
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-3 space-y-3">
                {draftsLoading && (
                  <div className="text-center py-8 text-slate-500 text-[12px]"
                       data-testid="outreach-drafts-loading">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Carregando…
                  </div>
                )}
                {!draftsLoading && drafts.length === 0 && (
                  <div className="text-center py-10 text-slate-500 text-[12px] rounded-lg border border-dashed border-white/10"
                       data-testid="outreach-drafts-empty">
                    Nenhum rascunho pendente. Quando um Buying Signal com score ≥ 90 chegar
                    numa empresa que já está no Kanban, a Claire vai redigir aqui.
                  </div>
                )}
                {drafts.map((d) => (
                  <article key={d.id}
                    data-testid={`outreach-draft-${d.id}`}
                    className="rounded-lg border border-amber-400/30 bg-amber-500/5 p-3">
                    <header className="flex items-start gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-[10px] mb-1 flex-wrap">
                          <span className="uppercase tracking-widest font-bold text-amber-300">
                            ⚡ Score {d.signal_score} · {d.signal_category}
                          </span>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-400">{new Date(d.created_at).toLocaleString("pt-BR")}</span>
                        </div>
                        <div className="text-[12.5px] font-black text-white truncate">{d.subject}</div>
                        <div className="text-[11px] text-slate-300 truncate">
                          {d.recipient_name || "—"} · <span className="font-mono text-amber-200">{d.recipient_email}</span> · <b>{d.company_name}</b>
                        </div>
                        {d.signal_title && (
                          <div className="text-[10.5px] text-slate-500 mt-0.5 italic truncate">
                            Gatilho: {d.signal_title}
                          </div>
                        )}
                      </div>
                    </header>
                    <pre className="text-[11.5px] text-slate-300 whitespace-pre-wrap font-sans line-clamp-6 mt-1">{d.body}</pre>
                    <footer className="flex items-center gap-2 mt-3 pt-2 border-t border-white/5">
                      {d.signal_url && (
                        <a href={d.signal_url} target="_blank" rel="noreferrer"
                           className="text-[10.5px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1">
                          <ExternalLink className="w-2.5 h-2.5" /> Ver gatilho
                        </a>
                      )}
                      <button type="button" onClick={() => setDraftEditing({...d})}
                              data-testid={`outreach-draft-edit-${d.id}`}
                              className="ml-auto h-7 px-2.5 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30">
                        Editar
                      </button>
                      <button type="button" onClick={() => draftDiscard(d)}
                              data-testid={`outreach-draft-discard-${d.id}`}
                              className="h-7 px-2.5 rounded text-[11px] font-bold text-red-300 border border-red-400/30 hover:bg-red-500/10">
                        Descartar
                      </button>
                      <button type="button" onClick={() => draftSend(d)} disabled={draftSending[d.id]}
                              data-testid={`outreach-draft-send-${d.id}`}
                              className="h-7 px-3 rounded text-[11px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-40"
                              style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                        {draftSending[d.id]
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Enviando…</>
                          : <><Send className="w-3 h-3" /> Enviar agora</>}
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
              {draftEditing && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                     onClick={() => setDraftEditing(null)}
                     data-testid="outreach-draft-edit-modal">
                  <div className="w-full max-w-2xl rounded-2xl border border-white/10 p-5 space-y-3"
                       style={{ background: "#0B1120" }}
                       onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-[15px] font-extrabold text-white">Editar rascunho</h3>
                      <button onClick={() => setDraftEditing(null)}
                              className="w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <input value={draftEditing.subject || ""}
                           onChange={(e) => setDraftEditing({...draftEditing, subject: e.target.value})}
                           className="w-full h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-[12.5px] text-white focus:border-amber-400 outline-none" />
                    <textarea value={draftEditing.body || ""} rows={12}
                              onChange={(e) => setDraftEditing({...draftEditing, body: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[12px] text-white focus:border-amber-400 outline-none font-sans resize-y" />
                    {/* Iter246 · Signature preview footer — shows the block
                        that will be auto-appended at send time so the
                        operator sees exactly what the recipient will see. */}
                    <SignaturePreviewFooter html={signatureHtml}
                                                onEdit={() => setSignatureOpen(true)} />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setDraftEditing(null)}
                              className="h-9 px-4 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/5">
                        Cancelar
                      </button>
                      <button onClick={draftSaveEdit}
                              className="h-9 px-4 rounded-lg text-[12px] font-bold text-white"
                              style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                        Salvar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "sent" && (
            <div className="rounded-2xl border overflow-hidden"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                 data-testid="outreach-tab-sent">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <MailCheck className="w-4 h-4 text-[#DC2626]" />
                <div className="flex-1 text-[13px] font-extrabold text-white">
                  Enviados & Atividade ({Object.keys(deliveryMap).length})
                </div>
                {/* Iter208 — bulk resend of every failed/bounced item */}
                <button type="button" onClick={resendFailedBulk}
                        disabled={resendingBulk}
                        data-testid="outreach-resend-failed-bulk"
                        title={failedCount ? `Reenviar ${failedCount} email(s) com falha` : "Verificar fila de retry"}
                        className="h-7 px-2.5 rounded text-[11px] font-bold text-white disabled:opacity-40 inline-flex items-center gap-1"
                        style={{ background: failedCount > 0
                          ? "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)"
                          : "linear-gradient(135deg,#64748B 0%,#334155 100%)" }}>
                  {resendingBulk ? <><Loader2 className="w-3 h-3 animate-spin" /> Reenviando…</> :
                    <><RotateCcw className="w-3 h-3" /> Reenviar Falhos ({failedCount})</>}
                </button>
                <button type="button" onClick={loadDeliveryStatus}
                        data-testid="outreach-sent-refresh"
                        className="h-7 px-2.5 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {Object.keys(deliveryMap).length === 0 ? (
                  <div className="p-8 text-center text-[12px] text-slate-500">
                    Nenhum email enviado ainda. Volte para <b>Compose</b>, escolha um contato e envie via Resend.
                  </div>
                ) : (
                  <table className="w-full text-[11.5px]" data-testid="outreach-sent-table">
                    <thead>
                      <tr className="text-slate-400 text-[10.5px] uppercase tracking-widest border-b border-white/10">
                        <th className="px-4 py-2 text-left">Contato</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-left">Enviado</th>
                        <th className="px-4 py-2 text-left">Aberto</th>
                        <th className="px-4 py-2 text-left">Clicou</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(deliveryMap)
                        .sort(([, a], [, b]) => (b.sent_at || "").localeCompare(a.sent_at || ""))
                        .slice(0, 200).map(([pid, d], i) => {
                        const contact = contacts.find((c) => c.prospect_id === pid);
                        const isFailed = ["failed","bounced"].includes(d.status);
                        const attempts = d.attempts || (isFailed ? 3 : 1);
                        return (
                          <tr key={pid} data-testid={`outreach-sent-row-${i}`}
                              className="border-b border-white/5 hover:bg-white/5">
                            <td className="px-4 py-2">
                              <div className="text-white font-bold truncate max-w-[300px]">{contact?.name || contact?.email || pid.slice(0, 8)}</div>
                              <div className="text-[10px] text-slate-500 truncate max-w-[300px]">{contact?.company_name}</div>
                            </td>
                            <td className="px-4 py-2">
                              <div title={isFailed ? `Após ${attempts} tentativa(s)` : ""}
                                   data-testid={`outreach-sent-status-${i}`}>
                                <DeliveryChip status={d.status} />
                                {isFailed && (
                                  <div className="text-[9px] text-red-300 mt-0.5">
                                    Tentativas: {attempts}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{d.sent_at ? new Date(d.sent_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{d.opened_at ? new Date(d.opened_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{d.clicked_at ? new Date(d.clicked_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                            <td className="px-4 py-2 text-right">
                              {isFailed && d.send_id && (
                                <button type="button" onClick={() => resendSingle(d.send_id, contact?.email || d.to)}
                                        data-testid={`outreach-resend-single-${i}`}
                                        title="Reenviar este email"
                                        className="w-7 h-7 rounded flex items-center justify-center bg-amber-500/15 hover:bg-amber-500/35 text-amber-300">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ── Retry Queue tab (Iter229) ──────────────────────────── */}
          {activeTab === "retry" && (
            <RetryQueueTab
              onRetrySingle={(sendId, toEmail) => resendSingle(sendId, toEmail)}
              onEditRetry={(item) => setRetryDrawer({
                send_id: item.id, to: item.to, subject: item.subject || "",
                body: "", contact: contacts.find((c) => c.email === item.to),
                prospect_id: item.prospect_id,
                reason: "Body original não foi arquivado — edite antes de reenviar",
              })}
              onBulkResend={resendFailedBulk}
              busy={resendingBulk} />
          )}

          {/* ── Analytics tab (Iter197) ────────────────────────────── */}
          {activeTab === "analytics" && (
            <div className="space-y-4" data-testid="outreach-tab-analytics">
              {/* Iter263 · Vision vs Claire delta */}
              <VisionVsClaireCard />
              {(() => {
                const rows = Object.values(deliveryMap);
                const sent = rows.length;
                const delivered = rows.filter((r) => ["delivered","opened","clicked"].includes(r.status)).length;
                const opened = rows.filter((r) => ["opened","clicked"].includes(r.status)).length;
                const clicked = rows.filter((r) => r.status === "clicked").length;
                const bounced = rows.filter((r) => ["bounced","complained","failed"].includes(r.status)).length;
                const openRate  = sent ? Math.round((opened / sent) * 100) : 0;
                const clickRate = sent ? Math.round((clicked / sent) * 100) : 0;
                const bounceRate = sent ? Math.round((bounced / sent) * 100) : 0;
                const totalReplies = replies?.length || 0;
                const replyRate = sent ? Math.round((totalReplies / sent) * 100) : 0;
                const kpis = [
                  { id: "sent",      label: "Enviados",   value: sent,      color: "#94A3B8" },
                  { id: "delivered", label: "Entregues",  value: delivered, color: "#10B981", pct: sent ? Math.round((delivered/sent)*100) : 0 },
                  { id: "open",      label: "Abertos",    value: opened,    color: "#3B82F6", pct: openRate },
                  { id: "click",     label: "Cliques",    value: clicked,   color: "#8B5CF6", pct: clickRate },
                  { id: "reply",     label: "Respostas",  value: totalReplies, color: "#F59E0B", pct: replyRate },
                  { id: "bounce",    label: "Bounces",    value: bounced,   color: "#EF4444", pct: bounceRate },
                ];
                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
                         data-testid="outreach-analytics-kpis">
                      {kpis.map((k) => (
                        <div key={k.id} data-testid={`outreach-analytics-kpi-${k.id}`}
                             className="rounded-xl border border-white/10 p-4"
                             style={{ background: "rgba(15,23,42,0.7)" }}>
                          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400">{k.label}</div>
                          <div className="text-[24px] font-extrabold tabular-nums mt-1" style={{ color: k.color }}>{k.value.toLocaleString()}</div>
                          {k.pct !== undefined && (
                            <div className="text-[10.5px] text-slate-500 mt-0.5">{k.pct}%</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4"
                         style={{ background: "rgba(15,23,42,0.7)" }}>
                      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-400 mb-2">Como interpretar</div>
                      <ul className="text-[11.5px] text-slate-300 space-y-1 leading-relaxed">
                        <li>• <b className="text-emerald-400">Open rate</b> saudável em cold outbound B2B: <b>15-25%</b>. Abaixo disso, revise subject line.</li>
                        <li>• <b className="text-amber-400">Reply rate</b> alvo: <b>3-8%</b> em cold. Acima disso, você achou o ICP + ângulo certo.</li>
                        <li>• <b className="text-red-400">Bounce rate</b> aceitável: &lt; <b>2%</b>. Acima, sua lista precisa de validação (use Hunter Verify).</li>
                      </ul>
                    </div>

                    {/* Iter235 · Signature language (PT vs EN) analytics */}
                    {sigAnalytics && sigAnalytics.total_sends > 0 && (
                      <div className="rounded-2xl border p-4 space-y-3"
                           data-testid="outreach-signature-analytics"
                           style={{ background: "linear-gradient(135deg,rgba(30,58,138,0.20) 0%,rgba(15,23,42,0.7) 60%)",
                                    borderColor: "rgba(59,130,246,0.35)" }}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] uppercase tracking-widest text-blue-300 font-extrabold">
                              Assinatura por idioma · últimos {sigAnalytics.window_days}d
                            </span>
                            <span className="text-[10.5px] text-slate-500">
                              {sigAnalytics.total_sends} envio(s) analisados
                            </span>
                          </div>
                          {sigAnalytics.winner && sigAnalytics.winner.delta_vs_other != null && (
                            <div className="text-[11px] font-bold px-2 py-1 rounded"
                                 style={{ background: "rgba(16,185,129,0.15)", color: "#6EE7B7" }}
                                 data-testid="sig-winner-badge">
                              🏆 {sigAnalytics.winner.lang.toUpperCase()} vence
                              {" "}({sigAnalytics.winner.delta_vs_other > 0 ? "+" : ""}
                              {sigAnalytics.winner.delta_vs_other}pp reply-rate)
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {sigAnalytics.by_language.map((row) => {
                            const flag = row.lang === "pt" ? "🇧🇷" : "🇺🇸";
                            const label = row.lang === "pt" ? "Português" : "Inglês";
                            const isWinner = sigAnalytics.winner?.lang === row.lang
                                              && sigAnalytics.winner?.delta_vs_other != null
                                              && sigAnalytics.winner.delta_vs_other > 0;
                            return (
                              <div key={row.lang}
                                   data-testid={`sig-lang-${row.lang}`}
                                   className="rounded-xl border p-3"
                                   style={{ borderColor: isWinner ? "rgba(16,185,129,0.5)" : "rgba(255,255,255,0.10)",
                                            background: isWinner ? "rgba(16,185,129,0.06)" : "rgba(0,0,0,0.20)" }}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-[18px]">{flag}</span>
                                  <span className="text-[13px] font-extrabold text-white">{label}</span>
                                  <span className="ml-auto text-[10.5px] text-slate-400 tabular-nums">
                                    {row.sent} envios
                                  </span>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  <SigMetric label="Entrega" value={row.delivered_rate} suffix="%" color="#10B981" />
                                  <SigMetric label="Abertura" value={row.open_rate} suffix="%" color="#3B82F6" />
                                  <SigMetric label="Reply" value={row.reply_rate} suffix="%" color="#F59E0B"
                                              highlight={isWinner} />
                                  <SigMetric label="Bounce" value={row.bounce_rate} suffix="%" color="#EF4444" />
                                </div>
                                {row.replied > 0 && (
                                  <div className="mt-2 text-[10.5px] text-slate-400">
                                    <b className="text-amber-300 tabular-nums">{row.replied}</b> resposta(s) ·
                                    <b className="text-blue-300 tabular-nums"> {row.opened}</b> abertura(s)
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {sigAnalytics.by_language_tone && sigAnalytics.by_language_tone.length > 0 && (
                          <details className="border-t border-white/10 pt-2">
                            <summary className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold cursor-pointer hover:text-white">
                              Cross-tab: idioma × tom
                            </summary>
                            <table className="w-full text-[11.5px] mt-2" data-testid="sig-lang-tone-table">
                              <thead>
                                <tr className="text-slate-500 text-[10px] uppercase tracking-widest">
                                  <th className="text-left py-1">Idioma</th>
                                  <th className="text-left py-1">Tom</th>
                                  <th className="text-right py-1">Sent</th>
                                  <th className="text-right py-1">Reply%</th>
                                  <th className="text-right py-1">Open%</th>
                                  <th className="text-right py-1">Bounce%</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-white/5">
                                {sigAnalytics.by_language_tone.slice(0, 8).map((r, i) => (
                                  <tr key={i}>
                                    <td className="py-1 text-white">
                                      {r.lang === "pt" ? "🇧🇷 PT" : "🇺🇸 EN"}
                                    </td>
                                    <td className="py-1 text-slate-300 capitalize">{r.tone}</td>
                                    <td className="py-1 text-right tabular-nums text-slate-300">{r.sent}</td>
                                    <td className="py-1 text-right tabular-nums text-amber-300 font-bold">{r.reply_rate}%</td>
                                    <td className="py-1 text-right tabular-nums text-blue-300">{r.open_rate}%</td>
                                    <td className="py-1 text-right tabular-nums text-red-300">{r.bounce_rate}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </details>
                        )}
                      </div>
                    )}

                    {/* Iter198 — Breakdown by tone + template */}
                    {breakdown && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <BreakdownTable
                          title="Performance por Tom"
                          testid="outreach-analytics-by-tone"
                          rows={breakdown.by_tone || []}
                          nameKey="tone"
                          emptyMsg="Envios recentes não têm tom atribuído — nas próximas cartas, o tom vai aparecer aqui." />
                        <BreakdownTable
                          title="Performance por Template"
                          testid="outreach-analytics-by-template"
                          rows={breakdown.by_template || []}
                          nameKey="template_name"
                          emptyMsg="Nenhum envio veio de um template ainda — use um template salvo para começar a medir." />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </main>
      </div>

      {/* Save-as-template modal (Iter189) */}
      {saveModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             onClick={() => !savingTemplate && setSaveModalOpen(false)}
             data-testid="outreach-save-modal">
          <div className="relative w-full max-w-md rounded-2xl border p-5 space-y-3"
               style={{ background: "#0B1120", borderColor: "rgba(16,185,129,0.30)" }}
               onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setSaveModalOpen(false)}
                    disabled={savingTemplate}
                    data-testid="outreach-save-modal-close"
                    className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-widest font-bold text-emerald-300">
                <BookmarkPlus className="w-3.5 h-3.5" /> Novo template
              </div>
              <h3 className="text-[16px] font-extrabold text-white mt-1">Salvar carta como template</h3>
              <p className="text-[11.5px] text-slate-400 mt-1">
                Dá um nome fácil de reconhecer — depois é 1 clique pra aplicar em qualquer contato.
                Use <code className="bg-white/5 px-1 rounded text-[10.5px]">{`{{first_name}}`}</code>,{" "}
                <code className="bg-white/5 px-1 rounded text-[10.5px]">{`{{company}}`}</code>,{" "}
                <code className="bg-white/5 px-1 rounded text-[10.5px]">{`{{city}}`}</code> etc.
                pra personalizar dinâmico.
              </p>
            </div>
            <input autoFocus type="text" value={saveName}
                   onChange={(e) => setSaveName(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && saveTemplate()}
                   placeholder="Ex: Logística FL · Consultivo · Reunião 15min"
                   data-testid="outreach-save-name"
                   className="w-full h-10 px-3 rounded-lg bg-black/30 border border-white/10 text-[13px] text-white focus:border-emerald-400 outline-none" />
            <div className="text-[10.5px] text-slate-500">
              Assunto: <span className="text-slate-300">{subject.slice(0, 80)}{subject.length > 80 ? "…" : ""}</span>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={() => setSaveModalOpen(false)}
                      disabled={savingTemplate}
                      data-testid="outreach-save-cancel"
                      className="h-9 px-4 rounded-lg border border-white/15 text-[12.5px] font-bold text-slate-300 hover:bg-white/5 disabled:opacity-40">
                Cancelar
              </button>
              <button type="button" onClick={saveTemplate}
                      disabled={savingTemplate || saveName.trim().length < 2}
                      data-testid="outreach-save-confirm"
                      className="ml-auto h-9 px-4 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-2 disabled:opacity-40"
                      style={{ background: "linear-gradient(135deg,#10B981 0%,#065F46 100%)" }}>
                {savingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
                Salvar template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk send modal (Iter192) */}
      {bulkOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             onClick={() => !bulkRunning && setBulkOpen(false)}
             data-testid="outreach-bulk-modal">
          <div className="relative w-full max-w-2xl rounded-2xl border p-5 space-y-3"
               style={{ background: "#0B1120", borderColor: "rgba(16,185,129,0.30)" }}
               onClick={(e) => e.stopPropagation()}>
            {!bulkRunning && (
              <button type="button" onClick={() => setBulkOpen(false)}
                      data-testid="outreach-bulk-modal-close"
                      className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            )}
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-widest font-bold text-emerald-300">
                <Rocket className="w-3.5 h-3.5" /> Envio em lote
              </div>
              <h3 className="text-[16px] font-extrabold text-white mt-1">
                Disparar {bulkStats.ready} carta{bulkStats.ready === 1 ? "" : "s"} personalizada{bulkStats.ready === 1 ? "" : "s"}
              </h3>
              <p className="text-[11.5px] text-slate-400 mt-1">
                A Claire vai redigir uma carta única para cada contato baseada no cargo, empresa e
                segmento. Ritmo ~2 envios/s (limite Resend). Tom <b className="text-slate-300">{TONE_OPTIONS.find((t) => t.id === tone)?.label}</b> · Idioma <b className="text-slate-300">{LANGUAGE_OPTIONS.find((l) => l.id === language)?.label}</b>.
                {attachments.length > 0 && ` Anexos: ${attachments.length}.`}
              </p>
            </div>

            {/* Iter224 — pre-dispatch summary + only-with-email toggle. */}
            {!bulkRunning && !bulkProgress && (
              <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 space-y-2"
                   data-testid="outreach-bulk-summary">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <BulkStat label="Selecionados" value={bulkStats.selected} tone="slate" testid="bulk-stat-selected" />
                  <BulkStat label="Com email"    value={bulkStats.with_email} tone="emerald" testid="bulk-stat-with-email" />
                  <BulkStat label="Sem email"    value={bulkStats.no_email} tone={bulkStats.no_email > 0 ? "amber" : "slate"} testid="bulk-stat-no-email" />
                  <BulkStat label="Prontos p/ enviar" value={bulkStats.ready} tone={bulkStats.ready > 0 ? "emerald" : "red"} testid="bulk-stat-ready" strong />
                </div>
                {bulkStats.blacklisted > 0 && (
                  <div className="text-[10.5px] text-red-300"
                       data-testid="outreach-bulk-blacklist-note">
                    ⚠ {bulkStats.blacklisted} email{bulkStats.blacklisted === 1 ? "" : "s"} bloqueado{bulkStats.blacklisted === 1 ? "" : "s"} (unsubscribe / hard bounce) — {bulkStats.blacklisted === 1 ? "será" : "serão"} ignorado{bulkStats.blacklisted === 1 ? "" : "s"}.
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-white/5">
                  <label className="text-[11px] text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={bulkOnlyWithEmail}
                      onChange={(e) => setBulkOnlyWithEmail(e.target.checked)}
                      data-testid="outreach-bulk-only-with-email"
                      className="w-3.5 h-3.5 rounded accent-emerald-500" />
                    Apenas contatos com email
                  </label>
                  {bulkStats.no_email > 0 && (
                    <button type="button" onClick={removeSelectedWithoutEmail}
                      data-testid="outreach-bulk-remove-noemail"
                      className="ml-auto h-7 px-2 rounded border border-white/15 text-[10.5px] font-bold text-slate-100 hover:border-emerald-400 inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Remover {bulkStats.no_email} sem email
                    </button>
                  )}
                  {bulkStats.unverified_unsent > 0 && (
                    <button type="button"
                      onClick={deleteSelectedUnverified}
                      disabled={cleaningFailed}
                      data-testid="outreach-bulk-delete-unverified"
                      title="Excluir permanentemente os leads não-confirmados desta seleção que ainda não foram enviados"
                      className={`h-7 px-2 rounded text-[10.5px] font-black text-white inline-flex items-center gap-1 disabled:opacity-40 ${bulkStats.no_email > 0 ? "" : "ml-auto"}`}
                      style={{ background: "linear-gradient(135deg,#F97316 0%,#B45309 100%)" }}>
                      {cleaningFailed
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Excluindo…</>
                        : <><Trash2 className="w-3 h-3" /> Excluir {bulkStats.unverified_unsent} não-confirmados</>}
                    </button>
                  )}
                </div>
              </div>
            )}

            {!bulkRunning && !bulkProgress && (
              <>
                {/* Iter193 — template selector */}
                <div className="rounded-lg border border-white/10 bg-black/30 p-2 space-y-1"
                     data-testid="outreach-bulk-template-picker">
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
                    <LibraryBig className="w-3 h-3" /> Modelo (opcional — evita LLM por contato)
                  </label>
                  <select value={bulkTemplateId}
                          onChange={(e) => setBulkTemplateId(e.target.value)}
                          data-testid="outreach-bulk-template-select"
                          className="w-full h-9 px-2 rounded-md bg-black/40 border border-white/10 text-[12px] text-white focus:border-emerald-400 outline-none">
                    <option value="">🪄 Claire (personaliza por contato)</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        📋 {t.name} · {t.language === "pt" ? "PT" : t.language === "en" ? "EN" : "ES"} · {t.tone}
                      </option>
                    ))}
                  </select>
                  {bulkTemplateId && (
                    <p className="text-[10px] text-emerald-300/80 mt-0.5">
                      Placeholders {"{{first_name}}"}, {"{{company}}"}, {"{{city}}"} serão substituídos por contato.
                    </p>
                  )}
                </div>
                {/* Iter208 — bulk filter dropdowns (email status / source / tier) */}
                <div className="grid grid-cols-3 gap-2" data-testid="outreach-bulk-filters">
                  <select value={bulkFilterVerified}
                          onChange={(e) => setBulkFilterVerified(e.target.value)}
                          data-testid="outreach-bulk-filter-verified"
                          className="h-8 px-2 rounded bg-black/40 border border-white/10 text-[11px] text-white">
                    <option value="">✉ Todos emails</option>
                    <option value="verified">✅ Verificados</option>
                    <option value="unverified">⚠ Não confirmados</option>
                    <option value="unresolvable">📦 Do repositório</option>
                  </select>
                  <select value={bulkFilterSource}
                          onChange={(e) => setBulkFilterSource(e.target.value)}
                          data-testid="outreach-bulk-filter-source"
                          className="h-8 px-2 rounded bg-black/40 border border-white/10 text-[11px] text-white">
                    <option value="">🔎 Toda fonte</option>
                    <option value="apollo">Apollo</option>
                    <option value="hunter">Hunter</option>
                    <option value="sunbiz">Sunbiz</option>
                    <option value="import">Import manual</option>
                  </select>
                  <select value={bulkFilterTier}
                          onChange={(e) => setBulkFilterTier(e.target.value)}
                          data-testid="outreach-bulk-filter-tier"
                          className="h-8 px-2 rounded bg-black/40 border border-white/10 text-[11px] text-white">
                    <option value="">🏷 Todos tiers</option>
                    <option value="hot">🔥 Hot</option>
                    <option value="warm">🌡 Warm</option>
                    <option value="cold">❄ Cold</option>
                  </select>
                </div>
                {/* Iter208 — blacklist warning banner (visible when >=1 selected contact is blacklisted) */}
                {bulkBlacklistCount > 0 && (
                  <div className="rounded-lg border border-red-400/40 bg-red-500/10 p-2.5 text-[11.5px] text-red-200 flex items-start gap-2"
                       data-testid="outreach-bulk-blacklist-warn">
                    <Ban className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <b>{bulkBlacklistCount}</b> contato(s) desta lista estão na blacklist e serão <b>ignorados automaticamente</b>.
                      <button type="button" onClick={() => window.open("/smith/blacklist", "_blank")}
                              className="ml-1 underline hover:text-white">Revisar blacklist</button>
                    </div>
                  </div>
                )}
                <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10 p-2 space-y-1"
                     data-testid="outreach-bulk-preview">
                  {bulkFilteredContacts.slice(0, 25).map((c, i) => {
                    const isBl = blacklistedSet.has((c.email||"").toLowerCase());
                    return (
                    <div key={i} className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${isBl?"bg-red-500/10 line-through opacity-60":"bg-white/[0.03]"}`}>
                      <span className="text-slate-500 tabular-nums w-6">{i + 1}.</span>
                      <span className="text-white font-bold truncate flex-1">{c.name || "(sem nome)"}</span>
                      <span className="text-slate-400 truncate max-w-[120px]">{c.title || "—"}</span>
                      <span className="text-[#F87171] font-mono text-[10.5px] truncate max-w-[180px]">{c.email}</span>
                      {isBl && <span className="text-[9px] text-red-300 font-bold">BLOQUEADO</span>}
                    </div>
                  );})}
                  <div className="text-[10px] text-slate-500 pt-1">
                    Mostrando {Math.min(25, bulkFilteredContacts.length)} de {bulkFilteredContacts.length} contatos após filtros (selecionados: {selectedIds.size})
                  </div>
                </div>
              </>
            )}

            {(bulkRunning || bulkProgress) && bulkProgress && (
              <div className="space-y-2" data-testid="outreach-bulk-progress">
                <div className="flex items-center gap-2 text-[11.5px]">
                  <span className="text-emerald-300 font-bold">
                    {bulkProgress.counts?.sent || 0} / {bulkProgress.total} enviadas
                  </span>
                  {(bulkProgress.counts?.failed || 0) > 0 && (
                    <span className="text-red-300 font-bold">
                      · {bulkProgress.counts?.failed} falhas
                    </span>
                  )}
                  {bulkRunning && <Loader2 className="w-3 h-3 animate-spin text-emerald-400 ml-auto" />}
                </div>
                {/* Progress bar */}
                <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all"
                       style={{ width: `${bulkProgress.total ? ((bulkProgress.counts?.sent || 0) / bulkProgress.total) * 100 : 0}%` }} />
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                  {(bulkProgress.items || []).map((it, i) => (
                    <div key={i}
                         data-testid={`outreach-bulk-item-${i}`}
                         className="flex items-center gap-2 px-2 py-1 text-[11px]">
                      <span className="tabular-nums text-slate-500 w-6">{it.index + 1}.</span>
                      <span className="truncate flex-1 text-slate-200">{it.name || it.to_email}</span>
                      <span className="text-[10.5px] text-slate-500 max-w-[220px] truncate italic">
                        {(it.subject || "").slice(0, 50)}
                      </span>
                      <DeliveryChip status={it.status === "sent" ? "sent" : it.status} testid={`outreach-bulk-status-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={() => setBulkOpen(false)}
                      disabled={bulkRunning}
                      data-testid="outreach-bulk-cancel"
                      className="h-9 px-4 rounded-lg border border-white/15 text-[12.5px] font-bold text-slate-300 hover:bg-white/5 disabled:opacity-40">
                {bulkProgress?.status === "completed" ? "Fechar" : "Cancelar"}
              </button>
              {!bulkProgress && (
                <button type="button" onClick={runBulkSend}
                        disabled={bulkRunning || bulkStats.ready === 0}
                        data-testid="outreach-bulk-confirm"
                        title={bulkStats.ready === 0
                                ? "Nenhum contato pronto — verifique email e blacklist"
                                : `${bulkStats.ready} contato${bulkStats.ready === 1 ? "" : "s"} pronto${bulkStats.ready === 1 ? "" : "s"} para envio`}
                        className="ml-auto h-9 px-4 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-2 disabled:opacity-40"
                        style={{ background: "linear-gradient(135deg,#10B981 0%,#065F46 100%)" }}>
                  {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                  Disparar {bulkStats.ready} envio{bulkStats.ready === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Sender Identity Setup modal (Iter194) */}
      {senderOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             onClick={() => !senderBusy && setSenderOpen(false)}
             data-testid="outreach-sender-modal">
          <div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border p-5 space-y-4"
               style={{ background: "#0B1120", borderColor: "rgba(201,162,78,0.30)" }}
               onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setSenderOpen(false)}
                    disabled={senderBusy}
                    data-testid="outreach-sender-modal-close"
                    className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-widest font-bold text-[#C9A24E]">
                <Globe className="w-3.5 h-3.5" /> Sender Identity Setup
              </div>
              <h3 className="text-[16px] font-extrabold text-white mt-1">
                Configurar mborges@smithprotective.com como remetente
              </h3>
              <p className="text-[11.5px] text-slate-400 mt-1">
                Pra enviar do domínio real, o Resend precisa verificar 3 registros DNS em <b className="text-slate-300">smithprotective.com</b>.
              </p>
            </div>

            {/* Status pill */}
            <div className="flex items-center gap-3 flex-wrap">
              <span data-testid="outreach-sender-status-pill"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold ${
                      senderStatus?.verified
                        ? "bg-emerald-500/20 text-emerald-300"
                        : senderStatus?.resend_status === "not_registered"
                          ? "bg-slate-500/20 text-slate-300"
                          : "bg-yellow-500/20 text-yellow-200"
                    }`}>
                {senderStatus?.verified
                  ? <><CheckCircle2 className="w-3 h-3" /> Verificado</>
                  : senderStatus?.resend_status === "not_registered"
                    ? <><AlertCircle className="w-3 h-3" /> Não registrado</>
                    : <><Loader2 className="w-3 h-3" /> Pendente ({senderStatus?.resend_status || "…"})</>}
              </span>
              <span className="text-[11px] text-slate-500 font-mono">
                {senderStatus?.sender_name} &lt;{senderStatus?.sender_email}&gt;
              </span>
              {senderStatus?.dashboard_url && (
                <a href={senderStatus.dashboard_url} target="_blank" rel="noreferrer"
                   data-testid="outreach-sender-dashboard"
                   className="ml-auto text-[11px] font-bold text-[#C9A24E] inline-flex items-center gap-1 hover:underline">
                  Abrir Resend Dashboard <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Not registered → register button */}
            {senderStatus && senderStatus.resend_status === "not_registered" && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-2"
                   data-testid="outreach-sender-register-block">
                <p className="text-[12px] text-yellow-100">
                  <b>Passo 1</b>: registre o domínio no Resend. Após clicar, vamos gerar os 3 registros DNS que você precisa colar no seu registrar (GoDaddy, Cloudflare, Route53, etc).
                </p>
                <button type="button" onClick={registerDomain} disabled={senderBusy}
                        data-testid="outreach-sender-register-btn"
                        className="h-9 px-4 rounded-lg font-bold text-[12.5px] text-[#0B1120] inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#C9A24E 0%,#8B6914 100%)" }}>
                  {senderBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                  Registrar smithprotective.com no Resend
                </button>
              </div>
            )}

            {/* DNS records table */}
            {senderStatus && senderStatus.records && senderStatus.records.length > 0 && (
              <div className="space-y-2" data-testid="outreach-sender-records">
                <div className="flex items-center justify-between">
                  <div className="text-[10.5px] uppercase tracking-widest font-bold text-slate-400">
                    Passo 2 — Adicione estes registros no DNS de smithprotective.com
                  </div>
                  <button type="button" onClick={verifyDomain} disabled={senderBusy}
                          data-testid="outreach-sender-verify-btn"
                          className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                          style={{ background: "linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)" }}>
                    {senderBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Verificar agora
                  </button>
                </div>
                <div className="rounded-lg border border-white/10 overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-black/40">
                      <tr className="text-left text-slate-400 uppercase tracking-widest text-[9.5px]">
                        <th className="px-2 py-1.5">Tipo</th>
                        <th className="px-2 py-1.5">Nome</th>
                        <th className="px-2 py-1.5">Valor</th>
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {senderStatus.records.map((r, i) => (
                        <tr key={i} data-testid={`outreach-sender-record-${i}`} className="hover:bg-white/[0.03]">
                          <td className="px-2 py-2 text-slate-200 font-mono font-bold">{r.type || "—"}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-1">
                              <code className="text-[10px] text-slate-300 truncate max-w-[180px]">{r.name}</code>
                              <button onClick={() => copyToClipboard(r.name, "Nome")}
                                      data-testid={`outreach-sender-copy-name-${i}`}
                                      className="text-slate-500 hover:text-white">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-1">
                              <code className="text-[10px] text-slate-300 truncate max-w-[220px]">{r.value}</code>
                              <button onClick={() => copyToClipboard(r.value, "Valor")}
                                      data-testid={`outreach-sender-copy-value-${i}`}
                                      className="text-slate-500 hover:text-white shrink-0">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              r.status === "verified"
                                ? "bg-emerald-500/25 text-emerald-200"
                                : "bg-yellow-500/20 text-yellow-200"
                            }`}>
                              {r.status === "verified" ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Loader2 className="w-2.5 h-2.5" />}
                              {r.status || "pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10.5px] text-slate-500">
                  Depois de colar os registros, aguarde ~15 min (pode chegar a 30 min) e clique em <b className="text-slate-300">Verificar agora</b>.
                </p>
              </div>
            )}

            {/* Verified — env-flip instructions */}
            {senderStatus?.verified && !senderStatus?.sender_verified_env && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2"
                   data-testid="outreach-sender-env-flip">
                <p className="text-[12px] text-emerald-100">
                  <b>Passo 3</b> ✅ Domínio verificado! Agora seta essas envs em produção:
                </p>
                <pre className="text-[10.5px] text-emerald-200 bg-black/40 rounded p-2 font-mono whitespace-pre-wrap">SMITH_SENDER_EMAIL=mborges@smithprotective.com
SMITH_SENDER_NAME=Marcelo Borges
SMITH_REPLY_TO=mborges@smithprotective.com
SMITH_SENDER_VERIFIED=1</pre>
                <p className="text-[11px] text-emerald-100">
                  Redeploya o backend e o From: dos emails vai virar exatamente <b>Marcelo Borges &lt;mborges@smithprotective.com&gt;</b>.
                </p>
              </div>
            )}
            {senderStatus?.verified && senderStatus?.sender_verified_env && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                   data-testid="outreach-sender-fully-live">
                <p className="text-[12px] text-emerald-100">
                  ✅ Tudo pronto — enviando como <b>{senderStatus.sender_name} &lt;{senderStatus.sender_email}&gt;</b> direto do domínio verificado.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Replies drawer (Iter193) */}
      {repliesOpen && (
        <div className="fixed inset-0 z-[85] flex justify-end bg-black/60 backdrop-blur-sm"
             onClick={() => setRepliesOpen(false)}
             data-testid="outreach-replies-drawer">
          <aside className="w-full max-w-md h-full border-l flex flex-col"
                 style={{ background: "#0B1120", borderColor: "rgba(59,130,246,0.30)" }}
                 onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-[13px] font-extrabold text-white flex-1">
                Respostas ({replies.length})
                {unreadReplies > 0 && <span className="ml-1.5 text-[10.5px] text-[#3B82F6]">· {unreadReplies} não lidas</span>}
              </span>
              <button onClick={loadReplies}
                      data-testid="outreach-replies-refresh"
                      className="w-7 h-7 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setRepliesOpen(false)}
                      data-testid="outreach-replies-close"
                      className="w-7 h-7 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto" data-testid="outreach-replies-list">
              {replies.length === 0 ? (
                <div className="p-6 text-center text-[12px] text-slate-500"
                     data-testid="outreach-replies-empty">
                  <Mail className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  Nenhuma resposta ainda. O scanner IMAP verifica novas respostas a cada 15 min.
                </div>
              ) : replies.map((r, i) => (
                <button key={r.id} type="button" onClick={() => openReply(r)}
                        data-testid={`outreach-reply-${i}`}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition ${!r.read ? "bg-[#3B82F6]/5 border-l-2 border-l-[#3B82F6]" : ""}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    {!r.read && <span className="w-2 h-2 rounded-full bg-[#3B82F6] shrink-0" />}
                    <span className={`text-[12.5px] truncate flex-1 ${!r.read ? "font-extrabold text-white" : "font-bold text-slate-300"}`}>
                      {r.from_name || r.from_email}
                    </span>
                    <span className="text-[10px] text-slate-500 tabular-nums shrink-0">
                      {new Date(r.received_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <div className="text-[11.5px] text-slate-300 truncate mb-0.5">{r.subject || "(sem assunto)"}</div>
                  <div className="text-[10.5px] text-slate-500 line-clamp-2">{r.snippet}</div>
                </button>
              ))}
            </div>
            {replyBody && (
              <div className="border-t border-white/10 bg-black/40 p-4 space-y-2 max-h-[50%] overflow-y-auto"
                   data-testid="outreach-reply-detail">
                <div className="flex items-center gap-2">
                  <span className="text-[10.5px] uppercase tracking-widest font-bold text-[#3B82F6]">de</span>
                  <span className="text-[12px] font-bold text-white truncate">{replyBody.from_name || replyBody.from_email}</span>
                  <span className="text-[10.5px] font-mono text-[#F87171] truncate">&lt;{replyBody.from_email}&gt;</span>
                  <button onClick={() => setReplyBody(null)} className="ml-auto text-slate-500 hover:text-white"
                          data-testid="outreach-reply-detail-close">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-[12px] font-bold text-white">{replyBody.subject || "(sem assunto)"}</div>
                <div className="text-[11.5px] text-slate-300 whitespace-pre-wrap leading-relaxed">
                  {replyBody.body || replyBody.snippet}
                </div>
                <button onClick={() => replyToLead(replyBody)}
                        data-testid="outreach-reply-followup"
                        className="w-full h-9 rounded-lg text-[12px] font-bold text-white inline-flex items-center justify-center gap-2"
                        style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                  <Send className="w-3.5 h-3.5" /> Responder este lead
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Auto-Nudge SMS 48h modal (Iter195) */}
      {nudgeOpen && (
        <div className="fixed inset-0 z-[86] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={() => setNudgeOpen(false)}
             data-testid="outreach-auto-nudge-modal">
          <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border"
               style={{ background: "#0B1120", borderColor: "rgba(245,158,11,0.35)" }}
               onClick={(e) => e.stopPropagation()}>
            <header className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-extrabold text-white">Auto-Nudge SMS 48h</div>
                <div className="text-[11px] text-slate-400">
                  Manda SMS Twilio pro DM quando o e-mail não é aberto em 48h. Rodada a cada 30 min.
                </div>
              </div>
              <button onClick={() => setNudgeOpen(false)}
                      data-testid="outreach-auto-nudge-close"
                      className="w-8 h-8 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="p-5 space-y-5">
              {nudgeBusy && !nudgeCfg ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" />
                </div>
              ) : nudgeCfgDraft && (
                <>
                  {/* Enable toggle + summary */}
                  <div className="rounded-xl border p-4 flex items-start gap-4"
                       style={{ borderColor: nudgeCfg?.enabled ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.1)",
                                 background: nudgeCfg?.enabled ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.02)" }}>
                    <div className="flex-1">
                      <div className="text-[13px] font-extrabold text-white flex items-center gap-2">
                        {nudgeCfg?.enabled
                          ? <><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Auto-Nudge ATIVO</>
                          : <><AlertCircle className="w-4 h-4 text-slate-400" /> Auto-Nudge Desligado</>}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        Janela: {nudgeCfg?.min_hours}h – {nudgeCfg?.max_hours}h após envio · Max {nudgeCfg?.max_per_run} SMS/rodada
                        {nudgeCfg?.skip_quiet_hours && ` · Silencia ${nudgeCfg.quiet_start_hour_et}h–${nudgeCfg.quiet_end_hour_et}h ET`}
                      </div>
                    </div>
                    <button type="button"
                            data-testid="outreach-auto-nudge-toggle"
                            disabled={nudgeBusy}
                            onClick={() => saveNudgeCfg({ enabled: !nudgeCfg?.enabled })}
                            className={`h-9 px-4 rounded-lg text-[12px] font-extrabold text-white inline-flex items-center gap-1.5 ${nudgeBusy ? "opacity-50" : ""}`}
                            style={{ background: nudgeCfg?.enabled
                              ? "linear-gradient(135deg,#EF4444 0%,#991B1B 100%)"
                              : "linear-gradient(135deg,#10B981 0%,#065F46 100%)" }}>
                      {nudgeCfg?.enabled ? "Desligar" : "Ligar"}
                    </button>
                  </div>

                  {/* Template editor */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-[#F59E0B]">
                      Template SMS (placeholders: {"{{first_name}} {{company}} {{city}}"})
                    </label>
                    <textarea
                      data-testid="outreach-auto-nudge-template"
                      value={nudgeCfgDraft.sms_template || ""}
                      onChange={(e) => setNudgeCfgDraft({ ...nudgeCfgDraft, sms_template: e.target.value })}
                      rows={4}
                      maxLength={480}
                      className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-[12px] text-white outline-none focus:border-[#F59E0B]" />
                    <div className="flex items-center justify-between">
                      <div className="text-[10.5px] text-slate-500 tabular-nums">
                        {(nudgeCfgDraft.sms_template || "").length}/480 caracteres
                      </div>
                      <button type="button"
                              data-testid="outreach-auto-nudge-save-template"
                              disabled={nudgeBusy || nudgeCfgDraft.sms_template === nudgeCfg?.sms_template}
                              onClick={() => saveNudgeCfg({ sms_template: nudgeCfgDraft.sms_template })}
                              className="h-7 px-3 rounded text-[11px] font-bold text-white disabled:opacity-40"
                              style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                      Salvar template
                    </button>
                    </div>
                  </div>

                  {/* Window + rate controls */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400">Mín (h)</label>
                      <input type="number" min={1} max={336}
                             data-testid="outreach-auto-nudge-min-hours"
                             value={nudgeCfgDraft.min_hours || 48}
                             onChange={(e) => setNudgeCfgDraft({ ...nudgeCfgDraft, min_hours: Number(e.target.value) })}
                             className="mt-1 w-full h-8 px-2 rounded bg-black/40 border border-white/15 text-[12px] text-white outline-none" />
                    </div>
                    <div>
                      <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400">Máx (h)</label>
                      <input type="number" min={2} max={720}
                             data-testid="outreach-auto-nudge-max-hours"
                             value={nudgeCfgDraft.max_hours || 72}
                             onChange={(e) => setNudgeCfgDraft({ ...nudgeCfgDraft, max_hours: Number(e.target.value) })}
                             className="mt-1 w-full h-8 px-2 rounded bg-black/40 border border-white/15 text-[12px] text-white outline-none" />
                    </div>
                    <div>
                      <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400">Max/run</label>
                      <input type="number" min={1} max={100}
                             data-testid="outreach-auto-nudge-max-run"
                             value={nudgeCfgDraft.max_per_run || 25}
                             onChange={(e) => setNudgeCfgDraft({ ...nudgeCfgDraft, max_per_run: Number(e.target.value) })}
                             className="mt-1 w-full h-8 px-2 rounded bg-black/40 border border-white/15 text-[12px] text-white outline-none" />
                    </div>
                  </div>
                  <button type="button"
                          data-testid="outreach-auto-nudge-save-window"
                          disabled={nudgeBusy}
                          onClick={() => saveNudgeCfg({
                            min_hours: Number(nudgeCfgDraft.min_hours),
                            max_hours: Number(nudgeCfgDraft.max_hours),
                            max_per_run: Number(nudgeCfgDraft.max_per_run),
                            skip_quiet_hours: !!nudgeCfgDraft.skip_quiet_hours,
                          })}
                          className="w-full h-8 rounded text-[11px] font-bold text-white disabled:opacity-40"
                          style={{ background: "rgba(255,255,255,0.08)" }}>
                    Salvar janela + limites
                  </button>

                  {/* Preview pending */}
                  <div className="rounded-xl border border-white/10 overflow-hidden">
                    <header className="px-3 py-2 flex items-center gap-2 border-b border-white/10 bg-black/30">
                      <Eye className="w-3.5 h-3.5 text-[#F59E0B]" />
                      <span className="text-[11.5px] font-extrabold text-white flex-1">
                        Preview — {nudgePending?.picked || 0} candidatos elegíveis
                      </span>
                      <button type="button"
                              data-testid="outreach-auto-nudge-run-now"
                              disabled={nudgeBusy || (nudgePending?.picked || 0) === 0}
                              onClick={runNudgeNow}
                              className="h-7 px-3 rounded text-[11px] font-extrabold text-white inline-flex items-center gap-1 disabled:opacity-30"
                              style={{ background: "linear-gradient(135deg,#EF4444 0%,#991B1B 100%)" }}>
                        <Zap className="w-3 h-3" /> Rodar agora
                      </button>
                    </header>
                    <div className="max-h-56 overflow-y-auto"
                         data-testid="outreach-auto-nudge-preview-list">
                      {(nudgePending?.skipped || []).length === 0 ? (
                        <div className="px-3 py-6 text-center text-[11.5px] text-slate-500">
                          Nenhum e-mail elegível na janela {nudgeCfg?.min_hours}h–{nudgeCfg?.max_hours}h.
                        </div>
                      ) : (nudgePending.skipped).map((s, i) => (
                        <div key={i}
                             data-testid={`outreach-auto-nudge-preview-${i}`}
                             className="px-3 py-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-3 h-3 text-[#F59E0B]" />
                            <span className="text-[11.5px] font-bold text-white truncate flex-1">
                              {s.phone || "—"}
                            </span>
                            <span className="text-[9.5px] text-slate-500">
                              email enviado {s.email_sent_at ? new Date(s.email_sent_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                            </span>
                          </div>
                          <div className="text-[10.5px] text-slate-400 line-clamp-2 mt-0.5 pl-5">
                            {s.preview_body}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent nudges + runs */}
                  {nudgeHistory && ((nudgeHistory.recent_nudges?.length || 0) > 0 || (nudgeHistory.runs?.length || 0) > 0) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-white/10 p-3">
                        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                          Últimos SMS
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="outreach-auto-nudge-recent-nudges">
                          {(nudgeHistory.recent_nudges || []).slice(0, 5).map((n, i) => (
                            <div key={n.id || i} className="text-[10.5px] text-slate-300">
                              <span className="tabular-nums text-slate-500 mr-1.5">
                                {new Date(n.sent_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                              </span>
                              → <span className="font-mono">{n.to}</span>
                            </div>
                          ))}
                          {(nudgeHistory.recent_nudges || []).length === 0 && (
                            <div className="text-[10.5px] text-slate-500">Nenhum SMS enviado ainda.</div>
                          )}
                        </div>
                      </div>
                      <div className="rounded-xl border border-white/10 p-3">
                        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                          Últimas rodadas
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="outreach-auto-nudge-recent-runs">
                          {(nudgeHistory.runs || []).slice(0, 5).map((r, i) => (
                            <div key={i} className="text-[10.5px] text-slate-300 flex items-center gap-1.5">
                              <span className="tabular-nums text-slate-500">
                                {new Date(r.finished_at || r._ts).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                              </span>
                              <span className={`px-1 rounded ${r.sent > 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>
                                {r.sent}/{r.picked}
                              </span>
                              <span className="text-slate-500 truncate">{r.triggered_by}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dedupe modal (Iter196) — shared component */}
      <DedupeModal open={dedupeOpen} onClose={() => setDedupeOpen(false)}
                    onApplied={() => { loadContacts(); }} />

      {/* Signature editor (Iter228) */}
      {signatureOpen && (
        <SignatureEditor
          onClose={() => setSignatureOpen(false)}
          onSaved={loadSignature} />
      )}

      {/* Retry Drawer (Iter228) — edit body/subject before re-sending a failed send */}
      {retryDrawer && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end"
             data-testid="retry-drawer"
             style={{ background: "rgba(2,6,23,0.6)" }}
             onClick={() => setRetryDrawer(null)}>
          <div className="w-full max-w-[720px] max-h-[92vh] sm:max-h-none sm:h-full flex flex-col shadow-2xl mobile-sheet"
               style={{ background: "linear-gradient(180deg,#0B1120 0%,#0F172A 100%)" }}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 shrink-0">
              <div className="min-w-0 flex-1">
                <h2 className="text-white text-[15px] font-black">Editar & Reenviar</h2>
                <p className="text-[11px] text-amber-300 truncate">{retryDrawer.reason}</p>
                <p className="text-[10.5px] text-slate-400 truncate">
                  Para: {retryDrawer.to} · {retryDrawer.contact?.name || retryDrawer.contact?.company_name}
                </p>
                {retryDrawer.vision_source?.analysis_id && (
                  <p className="text-[10.5px] mt-1 inline-flex items-center gap-1"
                     data-testid="retry-drawer-vision-badge">
                    <span className="px-1.5 py-0.5 rounded bg-purple-500/25 text-purple-200 text-[9.5px] font-black uppercase tracking-widest">
                      Gerado por Vision
                    </span>
                    <span className="text-slate-400 truncate max-w-[180px] sm:max-w-[280px]">
                      {retryDrawer.vision_source.preset_label || "Site audit"}
                    </span>
                  </p>
                )}
              </div>
              <button onClick={() => setRetryDrawer(null)} data-testid="retry-drawer-close-btn"
                      className="w-11 h-11 sm:w-8 sm:h-8 rounded hover:bg-white/10 text-slate-400 flex items-center justify-center shrink-0 touch-target">
                <X className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>
            </div>
            <div className="flex-1 p-6 space-y-3 overflow-y-auto">
              <label className="block">
                <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-widest mb-1">Assunto</div>
                <input value={retryDrawer.subject}
                  onChange={(e) => setRetryDrawer({ ...retryDrawer, subject: e.target.value })}
                  data-testid="retry-drawer-subject"
                  className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-white"
                  placeholder="Assunto do email" />
              </label>
              <label className="block">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-widest">Corpo do email</div>
                  <div className="flex items-center gap-1.5">
                    <button type="button"
                      onClick={() => setImportHubOpen(true)}
                      data-testid="retry-drawer-imports-btn"
                      title="Abrir Import Hub — templates e mídia"
                      className="h-7 px-2.5 rounded-lg text-[11px] font-black text-slate-200 border border-white/15 hover:border-[#DC2626] hover:text-white inline-flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> Imports
                    </button>
                    {(retryDrawer.prospect_id || retryDrawer.contact?.prospect_id) && (
                      <button type="button"
                        onClick={() => prefillRetryFromVision({
                          send_id: retryDrawer.send_id,
                          prospect_id: retryDrawer.prospect_id
                            || retryDrawer.contact?.prospect_id,
                        })}
                        disabled={visionPrefilling}
                        data-testid="retry-drawer-vision-prefill-btn"
                        title="Usar a última análise Vision deste prospect"
                        className="h-7 px-2.5 rounded-lg text-[11px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                        style={{ background: "linear-gradient(135deg,#9333EA 0%,#4C1D95 100%)" }}>
                        {visionPrefilling
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Eye className="w-3 h-3" />}
                        Prefill da Vision
                      </button>
                    )}
                    <button type="button"
                      onClick={runClaireDraft}
                      disabled={claireDrafting || !retryDrawer.subject || retryDrawer.subject.trim().length < 3}
                      data-testid="retry-drawer-claire-btn"
                      title="Claire redige o corpo com base no assunto e no destinatário"
                      className="h-7 px-2.5 rounded-lg text-[11px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                      style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                      {claireDrafting
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Sparkles className="w-3 h-3" />}
                      Gerar copy com a Claire
                    </button>
                  </div>
                </div>
                <textarea value={retryDrawer.body}
                  onChange={(e) => setRetryDrawer({ ...retryDrawer, body: e.target.value })}
                  data-testid="retry-drawer-body"
                  rows={16}
                  className="w-full rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-100 p-3 resize-vertical"
                  placeholder="Digite (ou cole) o corpo do email. A assinatura será anexada automaticamente." />
              </label>
              {/* Iter246 · Signature preview footer (retry drawer) */}
              <SignaturePreviewFooter html={signatureHtml}
                                            onEdit={() => setSignatureOpen(true)} />

              {/* Iter262 · Vision inline images ready to auto-attach */}
              {retryDrawer.vision_source?.has_inlineable_images
                 && (retryDrawer.vision_source?.image_urls?.length > 0) && (
                <div className="rounded-lg border border-purple-500/25 bg-purple-500/5 p-3 space-y-1.5"
                     data-testid="retry-drawer-vision-images">
                  <div className="text-[10.5px] font-black uppercase tracking-widest text-purple-300 inline-flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    {retryDrawer.vision_source.image_urls.length} foto(s) da Vision · auto-anexadas
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {retryDrawer.vision_source.image_urls.slice(0, 5).map((url, i) => (
                      <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                            alt={retryDrawer.vision_source.image_labels?.[i] || `foto ${i+1}`}
                            data-testid={`retry-drawer-vision-thumb-${i}`}
                            className="w-16 h-16 object-cover rounded border border-purple-500/40" />
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    💡 Cada foto vai como <b className="text-purple-300">imagem inline (CID)</b> no email — visível direto no corpo pelo destinatário.
                  </div>
                </div>
              )}

              <p className="text-[10.5px] text-slate-500">
                💡 Clique <b className="text-purple-300">Gerar copy com a Claire</b> para preencher o corpo contextualizado com o assunto e o destinatário — ou abra <b className="text-slate-300">Imports</b> para colar um template pronto.
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 px-4 sm:px-6 py-3 pb-safe border-t border-white/10 shrink-0"
                 style={{ background: "rgba(11,17,32,0.9)" }}>
              <button onClick={() => setRetryDrawer(null)}
                      className="h-11 sm:h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/10 sm:w-auto">
                Cancelar
              </button>
              <button type="button"
                onClick={() => resendSingle(retryDrawer.send_id, retryDrawer.to, {
                  body: retryDrawer.body,
                  subject: retryDrawer.subject,
                  ...(retryDrawer.vision_source?.analysis_id
                    ? {
                        source_kind: "vision_bridge",
                        ...(retryDrawer.vision_source?.has_inlineable_images
                          ? { inline_from_vision_id: retryDrawer.vision_source.analysis_id }
                          : {}),
                      }
                    : {}),
                })}
                disabled={!retryDrawer.body || retryDrawer.body.trim().length < 10}
                data-testid="retry-drawer-send-btn"
                className="h-11 sm:h-9 px-4 rounded-lg text-[12px] font-black text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                <RotateCcw className="w-3.5 h-3.5" />
                Reenviar agora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter254 · Import Hub — templates + media */}
      <ImportHubModal
        open={importHubOpen}
        onClose={() => setImportHubOpen(false)}
        onTemplateImported={(tpl) => {
          setTemplates((cur) => [tpl, ...cur.filter((t) => t.id !== tpl.id)]);
        }}
        onAssetImported={() => { /* asset URL is copied on demand */ }} />

      {/* Iter260 · Vision → Retry diff/preview modal */}
      {visionDiff && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6"
             data-testid="vision-diff-modal"
             style={{ background: "rgba(2,6,23,0.75)" }}
             onClick={() => setVisionDiff(null)}>
          <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border shadow-2xl overflow-hidden"
               style={{ background: "#0B1120", borderColor: "rgba(147,51,234,0.35)" }}
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-white/10 shrink-0 flex items-center justify-between">
              <div>
                <div className="text-[13.5px] font-black text-white inline-flex items-center gap-2">
                  <Eye className="w-4 h-4 text-purple-300" />
                  Revisar antes de substituir
                </div>
                <div className="text-[10.5px] text-slate-400 mt-0.5">
                  Vision → Retry Drawer · <b className="text-purple-300">{visionDiff.preset_label || "Site audit"}</b>
                  {visionDiff.company_name && <> · {visionDiff.company_name}</>}
                </div>
              </div>
              <button onClick={() => setVisionDiff(null)}
                      data-testid="vision-diff-close-btn"
                      className="w-8 h-8 rounded hover:bg-white/10 text-slate-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                    Assunto atual
                  </div>
                  <div className="text-[12px] text-slate-300 break-words" data-testid="vision-diff-current-subject">
                    {visionDiff.current_subject || <span className="text-slate-600">— (vazio)</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-purple-300 mb-1.5">
                    Novo assunto (Vision)
                  </div>
                  <div className="text-[12px] text-white break-words" data-testid="vision-diff-new-subject">
                    {visionDiff.subject}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                    Corpo atual · {(visionDiff.current_body || "").length} chars
                  </div>
                  <pre className="text-[11.5px] text-slate-300 whitespace-pre-wrap max-h-[280px] overflow-y-auto font-sans"
                       data-testid="vision-diff-current-body">
                    {visionDiff.current_body || "— (vazio)"}
                  </pre>
                </div>
                <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-purple-300 mb-1.5">
                    Novo corpo (Vision) · {(visionDiff.body || "").length} chars
                  </div>
                  <pre className="text-[11.5px] text-white whitespace-pre-wrap max-h-[280px] overflow-y-auto font-sans"
                       data-testid="vision-diff-new-body">
                    {visionDiff.body}
                  </pre>
                </div>
              </div>
              <p className="text-[10.5px] text-slate-500">
                💡 Ao aplicar, o assunto e o corpo do drawer serão substituídos.
                Você ainda poderá editar antes de reenviar.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 shrink-0"
                 style={{ background: "rgba(11,17,32,0.9)" }}>
              <button onClick={() => setVisionDiff(null)}
                      data-testid="vision-diff-cancel-btn"
                      className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/10">
                Cancelar
              </button>
              <button type="button"
                      onClick={() => applyVisionPrefill({
                        subject: visionDiff.subject,
                        body: visionDiff.body,
                        analysis_id: visionDiff.analysis_id,
                        preset_label: visionDiff.preset_label,
                        send_id: visionDiff.send_id,
                        prospect_id: visionDiff.prospect_id,
                        company_name: visionDiff.company_name,
                      })}
                      data-testid="vision-diff-apply-btn"
                      className="h-9 px-4 rounded-lg text-[12px] font-black text-white inline-flex items-center gap-1.5"
                      style={{ background: "linear-gradient(135deg,#9333EA 0%,#4C1D95 100%)" }}>
                <Eye className="w-3.5 h-3.5" />
                Aplicar Vision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({ testid, onClick, icon: Icon, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
            data-testid={testid}
            className="w-8 h-8 rounded-md border border-white/10 text-slate-300 hover:border-[#DC2626] hover:text-white flex items-center justify-center transition">
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function tierColor(t) {
  return { hot: "#DC2626", high: "#16A34A", qualified: "#EAB308",
           nurture: "#3B82F6", low: "#94A3B8" }[t] || "#94A3B8";
}

// Delivery-status chip mapping (Iter192). Colours climb the funnel:
//   sent → delivered → opened → clicked   (progressively greener/brighter)
//   bounced / complained / failed         (red)
function DeliveryChip({ status, testid }) {
  const { t } = useLang();
  const map = {
    queued:     { i18n: "so.deliv.queued",     icon: Loader2,             bg: "bg-slate-500/20",   fg: "text-slate-300",   spin: true },
    sent:       { i18n: "so.deliv.sent",       icon: CheckCircle2,        bg: "bg-emerald-500/15", fg: "text-emerald-300" },
    delivered:  { i18n: "so.deliv.delivered",  icon: MailCheck,           bg: "bg-emerald-500/25", fg: "text-emerald-200" },
    opened:     { i18n: "so.deliv.opened",     icon: Eye,                 bg: "bg-blue-500/25",    fg: "text-blue-200" },
    clicked:    { i18n: "so.deliv.clicked",    icon: MousePointerClick,   bg: "bg-purple-500/25",  fg: "text-purple-200" },
    bounced:    { i18n: "so.deliv.bounced",    icon: AlertOctagon,        bg: "bg-red-500/25",     fg: "text-red-200" },
    complained: { i18n: "so.deliv.complained", icon: AlertOctagon,        bg: "bg-red-500/30",     fg: "text-red-100" },
    failed:     { i18n: "so.deliv.failed",     icon: AlertCircle,         bg: "bg-red-500/25",     fg: "text-red-200" },
    // Iter248 · Resend sandbox reroute — email actually reached ops
    // inbox, not the original recipient. Amber to stand out.
    sent_sandbox_bypass: { i18n: "so.deliv.sandbox", icon: AlertCircle,   bg: "bg-amber-500/20",   fg: "text-amber-200" },
    skipped:    { i18n: "so.deliv.skipped",    icon: Ban,                 bg: "bg-slate-500/20",   fg: "text-slate-300" },
  };
  const m = map[status] || map.sent;
  const Icon = m.icon;
  const label = t(m.i18n);
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${m.bg} ${m.fg}`}
          title={t("so.deliv.tooltip", { label })}>
      <Icon className={`w-2.5 h-2.5 ${m.spin ? "animate-spin" : ""}`} />
      {label}
    </span>
  );
}


/**
 * Iter198 — Compact breakdown table used inside the Analytics tab.
 * Renders top-performing tones/templates ranked by reply_rate.
 * The `rows` array must contain the shape returned by
 * GET /smith/outreach/analytics/breakdown ({sent, replied, reply_rate, …}).
 */
// Iter235 · SigMetric — compact stat used by signature-language analytics
function SigMetric({ label, value, suffix = "", color, highlight = false }) {
  return (
    <div className="text-center rounded-md p-1.5"
         style={{ background: highlight ? "rgba(16,185,129,0.10)" : "transparent" }}>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-[14.5px] font-extrabold tabular-nums"
           style={{ color: highlight ? "#6EE7B7" : color }}>
        {value != null ? value : "—"}{suffix}
      </div>
    </div>
  );
}


function BreakdownTable({ title, rows, nameKey, emptyMsg, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4 overflow-hidden"
         style={{ background: "rgba(15,23,42,0.7)" }}
         data-testid={testid}>
      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-400 mb-2">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11.5px] text-slate-500 italic py-6 text-center">
          {emptyMsg}
        </div>
      ) : (
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="text-slate-500 text-[10px] uppercase tracking-widest border-b border-white/10">
              <th className="text-left py-1.5 pr-2">Nome</th>
              <th className="text-right py-1.5 px-1 tabular-nums">Env</th>
              <th className="text-right py-1.5 px-1 tabular-nums">Open</th>
              <th className="text-right py-1.5 px-1 tabular-nums">Reply</th>
              <th className="text-right py-1.5 pl-1 tabular-nums">Bounce</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 8).map((r, i) => (
              <tr key={i} data-testid={`${testid}-row-${i}`}
                  className="border-b border-white/5">
                <td className="py-1.5 pr-2 text-white font-bold truncate max-w-[140px]" title={r[nameKey]}>
                  {r[nameKey] || "—"}
                </td>
                <td className="py-1.5 px-1 text-right tabular-nums text-slate-300">{r.sent}</td>
                <td className={`py-1.5 px-1 text-right tabular-nums ${r.open_rate >= 15 ? "text-emerald-400 font-bold" : "text-slate-400"}`}>
                  {r.open_rate}%
                </td>
                <td className={`py-1.5 px-1 text-right tabular-nums ${r.reply_rate >= 3 ? "text-amber-300 font-bold" : "text-slate-400"}`}>
                  {r.reply_rate}%
                </td>
                <td className={`py-1.5 pl-1 text-right tabular-nums ${r.bounce_rate >= 2 ? "text-red-400 font-bold" : "text-slate-400"}`}>
                  {r.bounce_rate}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ── Iter224 · Compact stat card for the bulk pre-dispatch summary ────
function BulkStat({ label, value, tone = "slate", testid, strong = false }) {
  const palette = {
    slate:   { fg: "#CBD5E1", bg: "rgba(148,163,184,0.10)" },
    emerald: { fg: "#6EE7B7", bg: "rgba(16,185,129,0.10)" },
    amber:   { fg: "#FCD34D", bg: "rgba(245,158,11,0.12)" },
    red:     { fg: "#FCA5A5", bg: "rgba(239,68,68,0.10)" },
  };
  const c = palette[tone] || palette.slate;
  return (
    <div className={`rounded-lg px-2.5 py-1.5 border ${strong ? "border-white/20" : "border-white/5"}`}
         style={{ background: c.bg }}
         data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">
        {label}
      </div>
      <div className={`tabular-nums ${strong ? "text-[20px]" : "text-[15px]"} font-extrabold`}
           style={{ color: c.fg }}>
        {value}
      </div>
    </div>
  );
}



/**
 * Iter246 · Signature preview footer.
 *
 * Renders a compact, read-only preview of the operator's saved signature
 * inside the compose panel. Fixes the reported bug where operators
 * thought their signature wasn't being applied — the signature IS being
 * saved and appended server-side at send time, but was invisible in the
 * editor. Now the operator sees exactly what will be sent.
 */
function SignaturePreviewFooter({ html, onEdit }) {
  if (!html || html.replace(/<[^>]+>/g, "").trim().length < 3) {
    return (
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex items-center gap-2"
           data-testid="signature-preview-empty">
        <span>⚠ Nenhuma assinatura configurada — usando bloco padrão.</span>
        <button type="button" onClick={onEdit}
                data-testid="signature-preview-configure"
                className="ml-auto text-amber-300 font-bold hover:text-white">
          Configurar →
        </button>
      </div>
    );
  }
  return (
    <details className="group rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] overflow-hidden"
             data-testid="signature-preview-footer">
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center gap-2 text-[11px]">
        <span className="text-emerald-300 font-bold">✓ Assinatura será anexada</span>
        <span className="text-slate-400">— clique para pré-visualizar</span>
        <span className="ml-auto text-emerald-300 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="border-t border-emerald-500/20 bg-white text-slate-900 p-3 text-[12px]"
           data-testid="signature-preview-html"
           dangerouslySetInnerHTML={{ __html: html }} />
      <div className="border-t border-emerald-500/20 px-3 py-1.5 flex items-center justify-end">
        <button type="button" onClick={onEdit}
                data-testid="signature-preview-edit"
                className="text-[10.5px] text-emerald-300 hover:text-white font-bold">
          Editar assinatura →
        </button>
      </div>
    </details>
  );
}


// ── Iter263 · Vision vs Claire reply-rate delta card ─────────────────
function VisionVsClaireCard() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/smith/outreach/analytics/vision-vs-claire?days=180`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent — card fails soft */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API]);
  if (loading || !data) return null;
  const vision = data.vision_bridge || { sends: 0, reply_rate: 0, replies: 0 };
  const claire = data.claire || { sends: 0, reply_rate: 0, replies: 0 };
  const delta = data.delta_pp_vision_vs_claire;
  const noData = vision.sends === 0 && claire.sends === 0;
  return (
    <div className="rounded-xl border border-purple-500/30 bg-purple-500/[0.05] p-4"
         data-testid="analytics-vision-vs-claire">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-black uppercase tracking-widest text-purple-300 inline-flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" /> Vision × Claire · últimos 180d
        </div>
        {delta !== null && delta !== undefined && (
          <span className={`text-[10.5px] font-black px-2 py-0.5 rounded ${
            delta >= 0
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-red-500/20 text-red-300"
          }`}
                data-testid="analytics-vision-delta">
            {delta >= 0 ? "+" : ""}{delta.toFixed(1)} pp
          </span>
        )}
      </div>
      {noData ? (
        <div className="text-[12px] text-slate-400">
          Nenhum envio Vision/Claire tracked ainda. Após 3+ envios de cada, o delta aparecerá aqui.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <VisionTile label="Vision-prefilled" tone="purple"
                       sends={vision.sends} replies={vision.replies}
                       rate={vision.reply_rate}
                       testid="analytics-vision-vision-tile" />
          <VisionTile label="Claire draft" tone="violet"
                       sends={claire.sends} replies={claire.replies}
                       rate={claire.reply_rate}
                       testid="analytics-vision-claire-tile" />
          <VisionTile label="Manual / Template" tone="slate"
                       sends={(data.manual?.sends || 0) + (data.template?.sends || 0)}
                       replies={(data.manual?.replies || 0) + (data.template?.replies || 0)}
                       rate={
                         (((data.manual?.sends || 0) + (data.template?.sends || 0)) > 0)
                          ? Math.round(
                              (((data.manual?.replies || 0) + (data.template?.replies || 0)) /
                                 ((data.manual?.sends || 0) + (data.template?.sends || 0))) * 1000) / 10
                          : 0
                       }
                       testid="analytics-vision-manual-tile" />
        </div>
      )}
      {!data.has_enough_data && !noData && (
        <div className="text-[10px] text-slate-500 mt-1.5">
          💡 Aguarde 3+ envios em cada origem para o delta ficar estatisticamente honesto.
        </div>
      )}
    </div>
  );
}

function VisionTile({ label, tone, sends, replies, rate, testid }) {
  const palette = {
    purple: { fg: "#C4B5FD", bg: "rgba(147,51,234,0.14)", border: "rgba(147,51,234,0.45)" },
    violet: { fg: "#DDD6FE", bg: "rgba(124,58,237,0.10)", border: "rgba(124,58,237,0.30)" },
    slate:  { fg: "#CBD5E1", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.25)" },
  }[tone] || { fg: "#CBD5E1", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.25)" };
  return (
    <div className="rounded-lg p-2.5 border"
         style={{ background: palette.bg, borderColor: palette.border }}
         data-testid={testid}>
      <div className="text-[9.5px] font-black uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="text-[20px] font-black tabular-nums leading-tight"
           style={{ color: palette.fg }}>
        {rate}%
      </div>
      <div className="text-[10px] text-slate-500 tabular-nums">
        {replies}/{sends} replies
      </div>
    </div>
  );
}
